/**
 * O cliente do serviço `negocios` — os Funis e as Etapas de cada um (FR-1, FR-2, FR-68).
 *
 * O módulo espelha o serviço, um a um. **Nenhum tipo de contrato é digitado aqui** (AD-16):
 * todos são apelidos dos schemas gerados em `src/api/tipos-gerados/negocios.ts`. Alterou o
 * contrato do serviço, rode `npm run gerar-tipos` — o CI reprova a divergência.
 *
 * **Uma chave de cache, e uma só.** `GET /funis` devolve os Funis **com** as Etapas dentro:
 * quem lê este catálogo — o seletor de Funis, o quadro do Épico 3, a tela de Administração —
 * sempre precisa dos dois juntos. Por isso as sete mutações invalidam a mesma chave, e nenhuma
 * remenda o cache com a resposta: a resposta de uma mutação é **um** Funil (ou uma Etapa), e o
 * que a tela desenha é a lista inteira, com contagens que a mutação não recalcula.
 *
 * **Excluir não devolve corpo.** O serviço responde `204`: a exclusão é real (FR-68), e não há
 * linha para devolver. É o oposto do `DELETE` das listas de domínio, onde o corpo com
 * `ativo: false` é a prova de que a linha continua lá.
 *
 * As funções são chamadas autenticadas comuns: nenhuma passa `semRenovacao`, porque um acesso
 * expirado no meio de uma edição deve se resolver sozinho, sem jogar a pessoa de volta para a
 * tela de login com o trabalho perdido.
 *
 * **O Negócio entra na 3.2, com uma função só: criar — e ganha a leitura do quadro na 3.8.**
 * `GET /negocios?funil_id=` devolve os Negócios **em andamento** daquele Funil, já com a cor da
 * régua e os dias sem contato derivados no serviço (AD-10). Ler **um** Negócio continua sendo a
 * ficha (3.10), e continua sem função aqui porque continua sem rota do outro lado — é assim que o
 * recorte de cada história aparece no cliente.
 *
 * **Mover, ganhar, perder e excluir já têm rota, e continuam sem função — a razão é outra.**
 * Desde a 3.3 existe `PUT /negocios/{id}/etapa` (que é também o handoff Prospecção → Vendas),
 * desde a 3.4 `PUT /negocios/{id}/desfecho`, e desde a 3.5 `DELETE /negocios/{id}`. O que falta
 * às três é a **tela** que as chamaria: o quadro da 3.8 é leitura — o card não edita, não move,
 * não fecha e não exclui —, e a ficha é a 3.10. Um `moverNegocio` escrito antes dela seria código
 * morto. Do SPA, essas três histórias continuam mudando só os tipos gerados.
 *
 * **Criar Negócio invalida `CHAVE_DOS_FUNIS`, e não uma chave própria.** A contagem por Etapa
 * (`quantidade_de_negocios`) muda com a criação, e ela vive dentro do catálogo: sem a
 * invalidação, a tela de Administração continuaria mostrando a Etapa como vazia — e a ação de
 * exclusão dela, como liberada — depois de o Negócio já existir.
 *
 * **A régua de dias entra na 3.6, e ela tem chave de cache própria.** `GET /regua` e `GET /funis`
 * são dois recursos diferentes — a régua é a configuração que decide a cor de todos os Negócios,
 * e o catálogo é a estrutura de trabalho. Uma chave só faria o cartão da régua ser reinvalidado
 * a cada renomeação de Etapa, e o cartão dos Funis piscar a cada troca de corte.
 *
 * **E não há função que calcule a cor, nem aqui nem em componente nenhum.** Ela é derivada na
 * consulta, no serviço (AD-10), e chega pronta em cada item do quadro. Recalculá-la no SPA a
 * partir dos dias criaria uma segunda régua — que discordaria da primeira no minuto em que a
 * gestão mudasse os cortes entre a resposta e o desenho. O que este cliente lê de `GET /regua`
 * são os **cortes**, que é o que o cartão de Administração mostra e edita e o que a **legenda**
 * do quadro descreve.
 *
 * **A tela Hoje entra na 3.9, e ela é o quarto recurso.** `GET /hoje` devolve a leitura agregada
 * do dia — as **três** faixas da régua (contagem e soma de valor por cor, derivadas na consulta,
 * AD-10) e os Negócios em andamento mais parados, de todos os Funis que a policy entrega. Ele tem
 * chave de cache própria pela mesma razão que a régua tem a dela: é outro recurso, e não uma
 * leitura do quadro — o quadro é sempre de **um** Funil, e o painel não é de Funil nenhum.
 *
 * **E ele é invalidado por duas escritas, e não por nenhuma sua.** Criar Negócio muda as faixas
 * (uma contagem a mais, uma soma a mais) e pode mudar a lista; configurar a régua repinta **e
 * reconta**, porque a cor é derivada na consulta. Sem as duas invalidações a tela do dia mentiria
 * logo depois do ato — e é a primeira tela que se abre.
 *
 * **A ficha do Negócio entra na 3.10, e com ela o resto do épico chega ao cliente.** São três
 * rotas novas — `GET /negocios/{id}` (a ficha, com `valor`, `dias_sem_contato` e `cor`),
 * `GET /negocios/{id}/movimentacoes` (a trilha do AD-20) e `PUT /negocios/{id}` (os três campos
 * editáveis do FR-13) — e as **três** escritas que existiam sem cliente desde a 3.3, a 3.4 e a
 * 3.5: mover de Etapa (que é também o handoff Prospecção → Vendas), marcar o desfecho e excluir.
 * Elas estavam de fora porque a tela que as chamaria era esta; agora ela existe.
 *
 * **A ficha e a trilha têm chave própria, e as duas levam o `id` dentro.** São uma entrada por
 * Negócio: sem o `id` na chave, abrir a ficha de um e voltar para a de outro reescreveria a mesma
 * entrada e mostraria o Negócio errado por um quadro de renderização — o mesmo motivo pelo qual
 * `chaveDoQuadro` leva o `funil_id`.
 *
 * **E `useInvalidarOQuadroEOCatalogo` passou a invalidar cinco chaves, e não três.** Mover,
 * fechar e editar mudam **também** a ficha e a trilha daquele Negócio: mover troca a Etapa e
 * acrescenta uma linha ao histórico, fechar troca o `status` e a `data_de_conclusao`, editar troca
 * os três campos e o `atualizado_em`. Sem as duas novas, a tela que acabou de disparar o ato
 * continuaria mostrando o estado de antes dele — e é a tela em que o ato acontece.
 *
 * **A 3.11 acrescentou a lista de Originados (`GET /originados`), e com ela a quarta chamada da
 * invalidação.** A lista é leitura pura — não há botão nesta tela —, mas ela é escrita de outra:
 * `criar_negocio` é o único escritor de `sdr_id` (FR-6), e quem se nomeia SDR ao cadastrar um
 * Negócio em `/funil` é quem abre `/originados` em seguida. `CHAVE_DOS_ORIGINADOS` é de recurso
 * próprio, fora do prefixo do quadro, e por isso ela precisa ser nomeada.
 *
 * **A 4.1 acrescenta o catálogo de Tipos de Tarefa, e ele é o sexto recurso deste cliente.**
 * `GET /tipos-de-tarefa` devolve os Tipos com os Resultados de cada um, e as seis mutações são a
 * administração deles — só do Admin (`gerir_tipos_de_tarefa`, que o FR-61-A não dá nem ao
 * Gestor). A chave é própria, e não a do catálogo de Funis: são dois catálogos com guardas
 * diferentes e regras de exclusão **opostas** — Funil some de verdade (FR-68), Tipo fica com
 * `ativo: false` (FR-69), e é por isso que aqui o `DELETE` devolve corpo em vez de `204`.
 *
 * **A 4.2 traz a Tarefa, e ela é o sétimo recurso deste cliente.** `GET /tarefas?negocio_id=`
 * devolve as tarefas de um Negócio e `POST /tarefas` cria uma — com o título, o Tipo, o
 * Responsável e o vínculo do FR-20. A tela que as chama é o cartão de Tarefas da ficha (3.10), que
 * até aqui era um `CardReservado`: é o mesmo argumento que manteve `moverNegocio` fora daqui até a
 * ficha existir, agora do outro lado.
 *
 * **A chave das Tarefas fica sob `CHAVE_DOS_NEGOCIOS`, e a do catálogo não.** São coisas
 * diferentes: `CHAVE_DOS_TIPOS_DE_TAREFA` responde o **vocabulário** (o seletor de Tipo, que a
 * Administração também lê), e `CHAVE_DAS_TAREFAS` responde o **trabalho**. Estar sob o prefixo do
 * Negócio é o que faz excluir um Negócio deixar de mostrar as tarefas dele — elas somem no banco
 * pela cascata da FK, e o cache precisa acompanhar.
 *
 * **Concluir entrou na 4.4**, com o Resultado opcional do FR-17: `PUT /tarefas/{id}/conclusao`. Ela
 * é a primeira escrita deste módulo que invalida o prefixo **inteiro** de Negócios, e não só a
 * chave do recurso que ela tocou — a conclusão zera a régua de dias do Negócio (FR-12), e a cor
 * dela aparece na ficha, no quadro e no painel do dia.
 *
 * **Editar e excluir entraram na 11.6** (FR-80): `PUT`/`DELETE /tarefas/{id}`, inclusive a Tarefa
 * concluída, para o Responsável, o criador e o escopo `todas`; e o anexo
 * (`POST /tarefas/{id}/anexos`, multipart), servido só por `GET /tarefas/{id}/anexos/{anexo_id}`.
 * Reabrir continua não existindo.
 */

import {
  type UseQueryResult,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { criarApiDeCamposPersonalizados } from '@/api/campos-personalizados'
import { chamar, chamarComStatus, montarUrl } from '@/api/cliente'
import { ErroDaApi } from '@/api/erros'
import { chaveDaEmpresa } from '@/modulos/cadastro/api'
import type { components } from '@/api/tipos-gerados/negocios'

/** Os schemas do `openapi.json` do `negocios`, gerados por `npm run gerar-tipos` (AD-16). */
type Esquemas = components['schemas']

/** Um Funil com as Etapas dele, na ordem, e a soma dos Negócios das Etapas. */
export type Funil = Esquemas['RespostaDeFunil']

/** Uma Etapa: nome, posição e quantos Negócios estão parados nela. */
export type Etapa = Esquemas['RespostaDeEtapa']

/**
 * O que a tela envia para criar ou renomear um Funil: o nome, e só.
 *
 * Ele existe para ser o `satisfies` dos dois corpos — é ele, e não a boa vontade de quem
 * chama, que garante que nada além de `nome` chegue ao serviço, e que um `{ nom: ... }` não
 * compile.
 */
export type NomeDeFunil = Esquemas['CorpoDeNomeDeFunil']

/** O corpo da Etapa (11.4): nome e cor da paleta pastel — ou `null`, nenhuma cor. */
export type CorpoDaEtapa = Esquemas['CorpoDaEtapa']

/** O Funil novo com as Etapas dele, de uma vez (11.4). */
export type NovoFunil = Esquemas['CorpoDeNovoFunil']

/** A ordem nova: **todas** as Etapas do Funil, uma vez cada, na posição desejada. */
export type OrdemDasEtapas = Esquemas['CorpoDeOrdemDasEtapas']

/**
 * Um Negócio, como a rota de criação o devolve (FR-3, FR-13).
 *
 * **Sem campo de dinheiro**, e a ausência é do serviço: a coluna `valor` existe e nasce nula,
 * mas a varredura de sigilo do FR-62 casa `valor` por nome de campo — mesmo nulo — e roda como
 * SDR. Quem mostra dinheiro é a tela que tiver o recorte por perfil (3.8/3.10).
 *
 * `funil_id` vem aqui e **não** existe como coluna: ele é derivado de `etapa.funil_id`, e é
 * assim que o FR-4 ("sempre em um único Funil") deixa de ser regra e vira estado impossível de
 * representar.
 */
export type Negocio = Esquemas['RespostaDeNegocio']

/**
 * O que a tela envia para criar um Negócio.
 *
 * Ele existe para ser o `satisfies` do corpo — é ele, e não a boa vontade de quem chama, que
 * garante que nada além dos sete campos chegue ao serviço. `funil_id`, `status` e `valor` não
 * estão nele de propósito: o Funil sai da Etapa, ganhar e perder são a 3.4, e o dinheiro é do
 * Épico 5. Enviá-los é `422`, e não um campo ignorado em silêncio.
 */
export type NovoNegocio = Esquemas['CorpoDeNovoNegocio']

/**
 * Um card do quadro do Funil (3.8) — o item que `GET /negocios?funil_id=` devolve.
 *
 * **`valor` é opcional no tipo, e a opcionalidade é o FR-62.** O serviço serializa com
 * `exclude_unset`, e a resposta a uma **SDR** não carrega a chave — o tipo gerado a marca `?`
 * justamente por isso. São três estados que a tela distingue: `"148000.00"` (tem orçamento),
 * `null` ("sem orçamento", que é estado legítimo de Negócio em andamento) e **ausente** ("não é
 * da sua conta"). Nenhuma tela deve tratar os dois últimos como o mesmo.
 *
 * **O dinheiro vem como texto de duas casas**, e não como número: `numeric(14,2)` por JSON
 * number passaria por ponto flutuante no meio do caminho. Quem formata é a tela, com
 * `Intl.NumberFormat`.
 *
 * **`cor` vem do serviço**, derivada na consulta contra a régua vigente (AD-10), e
 * `dias_sem_contato` sai do mesmo instante e da mesma referência — os dois nunca se contradizem.
 *
 * Sem `empresa_razao_social` e sem `responsavel_nome`: eles são de outros serviços, e quem os
 * resolve é o SPA com o JWT de quem está olhando (AD-25). O nome que a policy do serviço dono não
 * devolver simplesmente não aparece, e o card cai no **código** do Negócio.
 */
export type NegocioNoQuadro = Esquemas['RespostaDeNegocioNoQuadro']

/**
 * A ficha de um Negócio (3.10) — o corpo de `GET /negocios/{id}`.
 *
 * São os **mesmos** dezesseis campos de `Negocio` mais os três que a régua e o orçamento
 * acrescentam: `valor`, `dias_sem_contato` e `cor`. Tipo próprio, e não `Negocio`: aquele é o
 * corpo das **escritas**, e a ausência de dinheiro nele é o recorte daquelas rotas.
 *
 * **`valor` é opcional no tipo, e a opcionalidade é o FR-62** — a mesma mecânica do card. O
 * serviço serializa com `exclude_unset`, e a resposta a uma **SDR** não carrega a chave. São três
 * estados que a tela distingue: `"148000.00"` (tem orçamento), `null` ("sem orçamento", que é
 * estado legítimo de Negócio em andamento) e **ausente** ("não é da sua conta"). Nenhuma tela deve
 * tratar os dois últimos como o mesmo.
 *
 * **Aqui o dinheiro tem as duas casas exatas**, e é a única resposta deste serviço que as tem: o
 * arredondamento de `MOEDA` (`apresentacao.ts`) é do card e da lista do dia, e não da ficha. Quem
 * precisa conferir centavo abre esta tela.
 *
 * **`sdr_id` vem aqui e não vem no card**, e a assimetria é o desenho: árvore nenhuma do quadro
 * desenha o SDR, e a ficha desenha. Nome de Empresa, de Responsável, de SDR, de Etapa e de Origem
 * não vêm — quem os resolve é o SPA, por lote, com o JWT de quem está olhando (AD-25).
 */
export type Ficha = Esquemas['RespostaDaFicha']

/**
 * Uma linha da trilha de movimentações (AD-20) — o item de `GET /negocios/{id}/movimentacoes`.
 *
 * **Sem `negocio_id`**: quem pediu a trilha já disse de qual Negócio ela é. **Sem campo dizendo
 * "isto é um handoff"**: a travessia é `de_funil_id !== para_funil_id`, e um booleano ao lado
 * seria uma segunda fonte para o mesmo fato, capaz de discordar dele.
 *
 * A lista vem **da mais recente para a mais antiga** e sai inteira — não há paginação, porque a
 * trilha de um Negócio é curta por construção. Nome de Etapa, de Funil e de autor são resolvidos
 * pelo SPA, por lote (AD-25).
 */
export type MovimentacaoDoNegocio = Esquemas['RespostaDeMovimentacao']

/**
 * O que a tela envia para editar um Negócio: a Previsão de fechamento e os campos personalizados.
 *
 * Ele existe para ser o `satisfies` do corpo — é ele, e não a boa vontade de quem chama, que
 * garante que nada além disso chegue ao serviço (Frete e Negociação saíram na 13.16). `valor`,
 * `responsavel_id`, `sdr_id`, `etapa_id` e `status` não estão nele de propósito: o preço é travado
 * pelo ERP (FR-44), trocar Responsável é o FR-32 (Épico 2), o SDR é congelado na criação (FR-6) e
 * mover e fechar têm porta própria. Enviá-los é `422`, e não um campo ignorado em silêncio.
 *
 * **Corpo completo, e não delta:** o que não vier volta ao nulo. É o mesmo desenho de
 * `EdicaoDeEmpresa`, e o preço é o de sempre — último a gravar vence.
 *
 * **A Previsão de fechamento (11.8, FR-78) é obrigatória aqui, e não opcional como no schema.** No
 * serviço ela tem default `null` — ausente desmarca —, e é justamente por isso que a tela não pode
 * esquecê-la: um "Salvar dados" que não a mandasse apagaria a previsão marcada. O tipo obriga quem
 * chama a dizer qual é (o id, ou `null` para nenhuma).
 */
export type EdicaoDeNegocio = Esquemas['CorpoDeEdicaoDeNegocio'] & {
  previsao_de_fechamento_id: string | null
}

/**
 * O que a tela envia para registrar a entrega (6.4, FR-74): a data, e só ela.
 *
 * O serviço recusa data no futuro com `422` — "entregue em" é fato passado —, e a mensagem dele é
 * a que a tela mostra, verbatim, como em todo o resto do SPA.
 */
export type RegistroDeEntrega = Esquemas['CorpoDaEntrega']

/**
 * O que a tela envia para mover o Negócio de Etapa: a Etapa destino, e só ela.
 *
 * **Desde a 11.5 só dentro do mesmo Funil**: Etapa de outro Funil é `409`. O handoff Prospecção →
 * Vendas é efeito da troca de Responsável (`TrocaDeResponsavel`), e de nada mais (AD-20).
 */
export type DestinoDoNegocio = Esquemas['CorpoDeMovimentacao']

/**
 * O corpo da troca de Responsável (11.5, FR-32) — só o novo Responsável. Não há chave de handoff:
 * o gatilho é o campo, e quem decide se o Negócio atravessa para Vendas é o servidor (FR-6, AD-18).
 */
export type TrocaDeResponsavel = Esquemas['CorpoDaTrocaDeResponsavel']

/** O corpo da troca da Empresa do Negócio (13.5, FR-83): a nova e, opcional, a que a ficha via. */
export type TrocaDeEmpresa = Esquemas['CorpoDaTrocaDeEmpresa']

/** O corpo do "Definir a empresa" do Negócio sem Empresa (13.20b1): só a Empresa escolhida. */
export type DefinicaoDaEmpresa = Esquemas['CorpoDaDefinicaoDaEmpresa']

/**
 * O que a tela envia para marcar o desfecho: `ganho` ou `perdido`, com o Motivo na perda.
 *
 * **Uma porta para os dois desfechos** (AD-13): o que muda entre ganhar e perder é o corpo, e não
 * o caminho. O `Literal` do serviço não aceita `em_andamento` — não existe, em contrato nenhum,
 * forma de pedir a reabertura de um Negócio (FR-10).
 *
 * `motivo_perda_id` é **obrigatório** na perda e **recusado** no ganho, e os dois casos são `422`.
 * A tela desabilita o confirmar enquanto não houver Motivo escolhido, para que a recusa nem
 * precise da ida à rede.
 */
export type DesfechoDoNegocio = Esquemas['CorpoDeDesfecho']

/**
 * Uma linha de "Negócios que pedem ação" (11.4): o card do quadro e os `motivos` que a puseram
 * na lista — no vermelho da régua, sem próxima tarefa, com tarefa vencida. `valor` segue o recorte
 * do card: ausente para a SDR (FR-62).
 */
export type NegocioQuePedeAcao = Esquemas['RespostaDeNegocioQuePedeAcao']

/** Por que o Negócio pede ação — os três cortes dos KPIs. */
export type MotivoDaAcao = NegocioQuePedeAcao['motivos'][number]

/**
 * O painel do dia — o corpo de `GET /hoje` desde a 11.4 (decisão 18): os números dos 4 KPIs
 * (`no_vermelho`, `sem_proximo_passo`, `tarefas_vencidas`, `tarefas_para_hoje`), a fila e a lista
 * `pedem_acao` (no máximo 10, do mais parado para o menos parado). As faixas de temperatura saíram.
 */
export type PainelDeHoje = Esquemas['RespostaDoPainelDeHoje']

/**
 * Uma linha da lista de Originados da SDR (3.11) — o item de `GET /originados`.
 *
 * **Sem campo de dinheiro, e a ausência não é recorte: é o contrato.** Ao contrário do card, da
 * faixa e da ficha — onde `valor` é opcional no tipo porque o serviço o some para a SDR —, aqui
 * `valor` não existe em resposta nenhuma, para perfil nenhum (FR-35, FR-62). Não há três estados a
 * distinguir, e não há texto de "valor não visível" a escrever.
 *
 * `motivo_perda_id` vem **cru**, e é nulo em todo Negócio que não foi perdido: `negocios` não
 * conhece o vocabulário do `cadastro` (AD-21, AD-22), e quem resolve o rótulo é a tela, pela mesma
 * escada de `useOpcoesDeDominio` → `useRotulosDeOpcoes` → rótulo genérico que a ficha já usa —
 * inclusive para o Motivo que a gestão excluiu depois (FR-67).
 *
 * **Nem `etapa_id`, nem `funil_id`.** A lista responde "em que pé está", e não "onde está".
 * Desde a 11.5 ela traz `responsavel_id` (o Responsável **atual** — "em andamento com quem"),
 * `sdr_id` (a visão da gestão identifica a SDR) e os nomes dos dois, `responsavel_nome` e
 * `sdr_nome`, lidos da réplica de usuário do serviço — que guarda os desligados, então a SDR que
 * saiu continua nomeada (nulos só quando a réplica não conhece o usuário). A linha segue o campo
 * SDR, que não muda.
 *
 * **Ela não leva à ficha.** Para a SDR, `GET /negocios/{id}` responde `404` sobre o Negócio já
 * entregue (a leitura nomeada vale só na lista); a gestão, que enxerga todo Negócio, abriria a
 * ficha com `200` — e um link que funcionasse para uns e quebrasse para outros seria pior que
 * nenhum.
 *
 * Nome de Empresa não vem — quem o resolve é o SPA, por lote, com o JWT de quem está olhando
 * (AD-25), e a linha cai no **código** do Negócio quando ele não vier.
 */
export type NegocioOriginado = Esquemas['RespostaDeNegocioOriginado']

/** Uma linha da lista "Aguardando entrega" (6.5, FR-75) — sem campo de dinheiro, por contrato. */
export type NegocioAguardandoEntrega = Esquemas['RespostaDeNegocioAguardandoEntrega']

/**
 * A régua de dias vigente: os dois cortes e desde quando eles valem (FR-12).
 *
 * `atualizado_em` vem junto e o cartão não o desenha hoje — ele está no contrato porque é o que
 * distingue "a gestão apertou a régua agora" de "ela está assim desde a instalação", e é o
 * campo que torna o ramo idempotente do serviço observável de fora.
 */
export type Regua = Esquemas['RespostaDeRegua']

/**
 * O que a tela envia para configurar a régua: os **dois** cortes, sempre.
 *
 * Ele existe para ser o `satisfies` do corpo — é ele, e não a boa vontade de quem chama, que
 * garante que nada além dos dois campos chegue ao serviço. Os dois são obrigatórios de propósito:
 * um `PUT` de um campo só compararia o amarelo novo com o vermelho velho, e a régua invertida
 * entraria pela porta que o validador de par existe para fechar.
 */
export type CortesDaRegua = Esquemas['CorpoDeRegua']

/**
 * Chave de cache do catálogo: uma só, porque a resposta traz os Funis e as Etapas juntos.
 *
 * É a mesma que o seletor de Funis vai ler quando ele chegar (3.2): a tela de Administração e
 * o seletor consultam o **mesmo** catálogo, e duas chaves fariam um mostrar o Funil que o
 * outro acabou de renomear.
 */
export const CHAVE_DOS_FUNIS = ['negocios', 'funis'] as const

/**
 * Chave de cache da régua — **própria**, e não a do catálogo.
 *
 * A régua é outro recurso: `GET /regua` responde dois números que valem para o serviço inteiro, e
 * `GET /funis` responde a estrutura de trabalho. Compartilhar a chave faria renomear uma Etapa
 * refazer a leitura da régua, e configurar a régua refazer a leitura do catálogo inteiro — duas
 * idas à rede que nenhuma das duas telas pediu.
 */
export const CHAVE_DA_REGUA = ['negocios', 'regua'] as const

/**
 * Prefixo de cache do **quadro** — próprio, e não a chave do catálogo.
 *
 * São dois recursos diferentes: `GET /funis` responde a estrutura de trabalho (as colunas) e
 * `GET /negocios` responde os cards. Compartilhar a chave faria renomear uma Etapa refazer a
 * leitura de centenas de Negócios, e criar um Negócio refazer a leitura do catálogo inteiro em
 * cima da que já é necessária.
 *
 * É **prefixo**, e não chave: há uma entrada por Funil, e quem invalida invalida todas — quem
 * cria um Negócio pode ter mudado o quadro de qualquer um dos dois.
 */
export const CHAVE_DOS_NEGOCIOS = ['negocios', 'quadro'] as const

/**
 * Chave de cache do quadro de **um** Funil.
 *
 * O `funilId` entra na chave porque a resposta é dele: sem isso, trocar de Funil na pílula
 * reescreveria a mesma entrada, e voltar mostraria os cards do outro quadro por um quadro de
 * renderização — exatamente o que o `?funil=` da URL existe para não deixar acontecer.
 */
export const chaveDoQuadro = ({ funilId }: { funilId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, funilId] as const

/**
 * Chave de cache dos Negócios **em andamento de uma Empresa** (11.12) — o "Abrir negócio" do
 * WhatsApp.
 *
 * Sob `CHAVE_DOS_NEGOCIOS`, como o quadro e a ficha: uma escrita de Negócio disparada de qualquer
 * tela (ganhar, perder, excluir no modal) invalida o prefixo, e o botão da conversa relê — um
 * Negócio que acabou de ser ganho deixa de ser oferecido.
 */
export const chaveDosNegociosDaEmpresa = ({ empresaId }: { empresaId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, 'empresa', empresaId] as const

/**
 * Chave de cache dos Negócios **em andamento e sem Empresa** de um contato (13.20b2) — o "Abrir
 * negócio" da conversa, o "+ Negócio para esta pessoa" e o painel Dados do contato.
 *
 * Sob `CHAVE_DOS_NEGOCIOS` pelo mesmo motivo da chave por Empresa: definir a Empresa, perder ou
 * excluir o Negócio invalida o prefixo, e o recorte relê — o Negócio que recebeu a Empresa sai dele.
 */
export const chaveDosNegociosDaPessoa = ({ pessoaId }: { pessoaId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, 'pessoa', pessoaId] as const

/**
 * Chave de cache da **ficha** de um Negócio (3.10).
 *
 * O `negocioId` entra na chave porque a resposta é dele: sem isso, abrir a ficha de um Negócio e
 * navegar para a de outro reescreveria a mesma entrada, e a segunda tela mostraria o primeiro por
 * um quadro de renderização — o mesmo motivo pelo qual `chaveDoQuadro` leva o `funil_id`.
 *
 * Ela fica **sob** `CHAVE_DOS_NEGOCIOS` de propósito: quem invalida o prefixo do quadro invalida
 * as fichas junto, e é isso que faz uma escrita disparada de qualquer tela deixar todas as
 * leituras de Negócio coerentes de uma vez.
 */
export const chaveDaFicha = ({ negocioId }: { negocioId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, 'ficha', negocioId] as const

/**
 * Chave de cache da **trilha** de um Negócio (3.10).
 *
 * Chave separada da ficha, e não um campo dentro dela: são duas rotas, e mover um Negócio muda as
 * duas por razões diferentes — a ficha porque o `etapa_id` trocou, a trilha porque ganhou uma
 * linha. Compartilhar a chave faria editar um campo do FR-13 (que não toca a trilha) reler o
 * histórico inteiro.
 */
export const chaveDasMovimentacoes = ({ negocioId }: { negocioId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, 'movimentacoes', negocioId] as const

/**
 * Chave de cache do painel do dia — **própria**, e não a do quadro nem a do catálogo.
 *
 * São recursos diferentes: `GET /negocios` responde os cards de **um** Funil, e `GET /hoje`
 * responde as faixas e a lista de **todos** eles. Compartilhar a chave do quadro faria abrir um
 * Funil reler o painel inteiro, e trocar de Funil na pílula invalidar uma resposta que não é de
 * Funil nenhum.
 *
 * Ela não tem prefixo com parâmetro: a resposta é a mesma para quem quer que a peça — o recorte é
 * do serviço, por sessão, e não da URL.
 */
export const CHAVE_DO_PAINEL_DE_HOJE = ['negocios', 'hoje'] as const

/**
 * Chave de cache da lista de Originados — **própria**, e não a do quadro nem a do painel.
 *
 * São recursos diferentes: `GET /negocios` responde os cards de **um** Funil, `GET /hoje` responde
 * o dia inteiro e `GET /originados` responde o que **quem está pedindo** originou, em todos os
 * Funis e nos três status. Compartilhar o prefixo do quadro faria abrir um Funil reler a lista, e
 * trocar de Funil na pílula invalidar uma resposta que não é de Funil nenhum.
 *
 * Ela não tem prefixo com parâmetro, como a do painel: a resposta é a mesma para quem quer que a
 * peça — o recorte é do serviço, por sessão (é a policy que lê `app.user_id`), e não da URL.
 */
export const CHAVE_DOS_ORIGINADOS = ['negocios', 'originados'] as const

/**
 * A chave da lista "Aguardando entrega" (6.5). De **recurso**, como a dos Originados: ela responde
 * uma pergunta própria do serviço, e é invalidada por toda escrita de Negócio — registrar a data
 * tira uma linha da lista, e ganhar um Negócio põe outra.
 */
export const CHAVE_DO_AGUARDANDO_ENTREGA = ['negocios', 'aguardando-entrega'] as const

function caminhoDoFunil(funilId: string): string {
  return `/funis/${encodeURIComponent(funilId)}`
}

function caminhoDaEtapa(funilId: string, etapaId: string): string {
  return `${caminhoDoFunil(funilId)}/etapas/${encodeURIComponent(etapaId)}`
}

/**
 * Lê os Funis com as Etapas de cada um (`200`).
 *
 * Só autenticada: o catálogo é o seletor de Funis, e ele aparece para todo perfil. Numa
 * instalação recém-migrada vêm os dois Funis de fábrica do FR-2, com três Etapas cada.
 */
export function lerFunis(): Promise<Funil[]> {
  return chamar<Funil[]>('negocios', '/funis')
}

/**
 * Cria o Funil (`201`) no fim da lista, **já com as Etapas pedidas**, numa transação (11.4).
 *
 * Exige `gerir_funis_e_etapas`; os demais recebem `403`. `409` quando o nome já existe, `422`
 * sem Etapa nenhuma, com nome vazio ou repetido na lista. Mostre a mensagem do serviço como veio.
 */
export function criarFunil(novo: NovoFunil): Promise<Funil> {
  return chamar<Funil>('negocios', '/funis', {
    method: 'POST',
    corpo: novo satisfies NovoFunil,
  })
}

/**
 * Troca o nome do Funil (`200`). As Etapas e os Negócios continuam onde estão.
 *
 * **Reenviar o nome que já está gravado não deixa registro na trilha** — o serviço compara
 * antes de gravar.
 */
export function renomearFunil(funilId: string, nome: string): Promise<Funil> {
  return chamar<Funil>('negocios', caminhoDoFunil(funilId), {
    method: 'PUT',
    corpo: { nome } satisfies NomeDeFunil,
  })
}

/**
 * Exclui o Funil (`204`) — as Etapas dele vão junto, por cascata do banco.
 *
 * `409` quando há Negócio em qualquer Etapa dele, e **nenhum** Negócio é movido (FR-68).
 */
export function excluirFunil(funilId: string): Promise<void> {
  return chamar<void>('negocios', caminhoDoFunil(funilId), { method: 'DELETE' })
}

/** Cria a Etapa no fim do Funil (`201`). `409` quando o nome já é de outra Etapa **dele**. */
export function criarEtapa(funilId: string, etapa: CorpoDaEtapa): Promise<Etapa> {
  return chamar<Etapa>('negocios', `${caminhoDoFunil(funilId)}/etapas`, {
    method: 'POST',
    corpo: etapa satisfies CorpoDaEtapa,
  })
}

/**
 * Grava nome e cor da Etapa (`200`) — corpo **completo**: cor ausente volta a "nenhuma" (11.4).
 * A linha é a mesma, e por isso os Negócios dela não se mexem. `404` quando a Etapa não existe
 * **ou** é de outro Funil.
 */
export function atualizarEtapa(
  funilId: string,
  etapaId: string,
  etapa: CorpoDaEtapa,
): Promise<Etapa> {
  return chamar<Etapa>('negocios', caminhoDaEtapa(funilId, etapaId), {
    method: 'PUT',
    corpo: etapa satisfies CorpoDaEtapa,
  })
}

/** Exclui a Etapa (`204`); `409` quando há Negócio nela, sem mover nada (FR-68). */
export function excluirEtapa(funilId: string, etapaId: string): Promise<void> {
  return chamar<void>('negocios', caminhoDaEtapa(funilId, etapaId), { method: 'DELETE' })
}

/**
 * Reescreve a ordem das Etapas do Funil (`200`), da lista **completa** que chegou.
 *
 * A lista completa é o desenho do serviço, e não um detalhe deste cliente: um "trocar duas"
 * abriria um estado intermediário inválido no banco. `422` quando a lista não cobre exatamente
 * as Etapas daquele Funil — id faltando, repetido ou de outro Funil.
 */
export function reordenarEtapas(funilId: string, etapas: readonly string[]): Promise<Etapa[]> {
  return chamar<Etapa[]>('negocios', `${caminhoDoFunil(funilId)}/etapas/ordem`, {
    method: 'PUT',
    corpo: { etapas: [...etapas] } satisfies OrdemDasEtapas,
  })
}

/**
 * Cria o Negócio (`201`), `em_andamento` e na Etapa escolhida.
 *
 * Exige `criar_negocio`, que o FR-61-A concede aos **quatro** perfis por padrão; um usuário de
 * quem o Admin a tenha retirado recebe `403`. `404` quando a Etapa não existe, `422` quando
 * falta um dos três uuid obrigatórios ou quando o corpo traz campo que o contrato não tem.
 *
 * **A Empresa precisa existir antes.** O AD-22 só permite chamada síncrona de leitura entre
 * serviços, então o `negocios` não cria Empresa: quem compõe as duas criações é esta tela, com
 * o JWT de quem está usando (AD-25). O preço é a criação não ser atômica — o resíduo possível é
 * uma Empresa sem Negócio, e o formulário reaproveita a Empresa já criada no reenvio para não
 * multiplicá-lo.
 *
 * Os campos vão **um a um** pela razão de sempre: o corpo do serviço é fechado.
 */
export function criarNegocio(negocio: NovoNegocio): Promise<Negocio> {
  return chamar<Negocio>('negocios', '/negocios', {
    method: 'POST',
    corpo: {
      empresa_id: negocio.empresa_id,
      etapa_id: negocio.etapa_id,
      responsavel_id: negocio.responsavel_id,
      sdr_id: negocio.sdr_id,
    } satisfies NovoNegocio,
  })
}

/**
 * Lê o quadro de um Funil (`200`): os Negócios **em andamento** dele, do mais parado para o mais
 * recente.
 *
 * Só autenticada, como `GET /funis`: quem recorta o que cada um enxerga é a RLS do serviço,
 * linha a linha — em Vendas o Vendedor vê o que é dele, na Prospecção vê todos (FR-30, FR-31). A
 * tela **não filtra nada** por conta própria.
 *
 * Cada item já traz `cor` e `dias_sem_contato` derivados no serviço (AD-10), e `valor` como
 * texto de duas casas — **exceto** para a SDR, cuja resposta não carrega a chave (FR-62).
 *
 * `404` quando o Funil não existe; `200` com `[]` quando ele não tem Negócio em andamento
 * visível — "não há nada aqui" é uma resposta, e não um recurso ausente.
 */
export function listarNegociosDoFunil(funilId: string): Promise<NegocioNoQuadro[]> {
  return chamar<NegocioNoQuadro[]>('negocios', `/negocios?funil_id=${encodeURIComponent(funilId)}`)
}

/**
 * Os Negócios **em andamento** de uma Empresa, em todos os Funis (`GET /negocios?empresa_id=`,
 * 11.12) — o mesmo card do quadro, com o mesmo recorte do FR-62 (a SDR não recebe `valor`).
 *
 * É a leitura do "Abrir negócio" da conversa de WhatsApp: a tela sabe a Empresa do contato, e não
 * o Funil. `200` com `[]` quando não há Negócio em andamento **que quem olha enxergue** — a RLS
 * recorta, e a tela não filtra nada.
 */
export function listarNegociosDaEmpresa(empresaId: string): Promise<NegocioNoQuadro[]> {
  return chamar<NegocioNoQuadro[]>(
    'negocios',
    `/negocios?empresa_id=${encodeURIComponent(empresaId)}`,
  )
}

/**
 * Os Negócios **em andamento e sem Empresa** de um contato (`GET /negocios?pessoa_id=`, 13.20b1) —
 * o mesmo card do quadro e o mesmo recorte do FR-62, sob a policy de quem pede.
 *
 * O Negócio que já recebeu a Empresa sai daqui (aparece pelo recorte da Empresa), e o fechado não
 * aparece em nenhum. `200` com `[]` quando não há um que quem olha enxergue — nunca `404`: a Pessoa
 * mora no `cadastro`.
 */
export function listarNegociosDaPessoa(pessoaId: string): Promise<NegocioNoQuadro[]> {
  return chamar<NegocioNoQuadro[]>(
    'negocios',
    `/negocios?pessoa_id=${encodeURIComponent(pessoaId)}`,
  )
}

function caminhoDoNegocio(negocioId: string): string {
  return `/negocios/${encodeURIComponent(negocioId)}`
}

/**
 * Lê a ficha de um Negócio (`200`) — o cabeçalho, os campos do FR-13 e o desfecho, quando houve.
 *
 * Só autenticada, como o quadro e o painel: quem recorta é a RLS do serviço, linha a linha. Em
 * Vendas o Vendedor que não é Responsável recebe **`404`**; na Prospecção, `200` (FR-30, FR-31).
 * `404` é também a resposta do Negócio inexistente — os dois casos são a mesma resposta de
 * propósito, porque um `403` contaria que existe uma ficha ali.
 *
 * Ela traz Negócio **fechado** tanto quanto em andamento, ao contrário do quadro: é assim que se
 * lê o desfecho.
 *
 * `valor` vem como texto de duas casas **exatas** — ou `null` ("sem orçamento"), ou a chave nem
 * vem (SDR, FR-62). `cor` e `dias_sem_contato` descrevem o mesmo instante (AD-10).
 *
 * **E o `404` tem uma segunda causa**, que não é o Negócio: a régua ausente, num database migrado
 * até a revisão anterior. As duas se distinguem pela mensagem do envelope — mostre-a como veio.
 */
export function lerFicha(negocioId: string): Promise<Ficha> {
  return chamar<Ficha>('negocios', caminhoDoNegocio(negocioId))
}

/**
 * Lê a trilha de movimentações de um Negócio (`200`), da mais recente para a mais antiga.
 *
 * Só autenticada, pela mesma razão da ficha. **`404`, e não lista vazia**, quando o Negócio não
 * existe ou não é visível: o serviço lê o Negócio antes de listar, para que "não há história" e
 * "não é seu" não respondam a mesma coisa. Negócio visível e nunca movido é `200` com `[]`.
 *
 * Sai inteira: não há paginação, porque a trilha de um Negócio é curta por construção.
 */
export function listarMovimentacoes(negocioId: string): Promise<MovimentacaoDoNegocio[]> {
  return chamar<MovimentacaoDoNegocio[]>('negocios', `${caminhoDoNegocio(negocioId)}/movimentacoes`)
}

/**
 * Grava os campos editáveis do Negócio (`200`) — a Previsão de fechamento em corpo **completo**; os
 * campos personalizados (FR-79) em **delta**. A Origem saiu na 11.3 (o Negócio herda a da
 * Empresa); Frete e Negociação, na 13.16 — as colunas ficam no banco, e este corpo não as apaga.
 *
 * Exige `editar_negocio`, que o FR-61-A concede aos quatro perfis (limitada para Vendedor e SDR,
 * e `limitada` passa pela guarda: o recorte é de linha, e não de rota); um usuário de quem o Admin
 * a tenha retirado recebe `403`. `404` quando o Negócio não existe **ou** a RLS o esconde. `409`
 * quando ele já está ganho ou perdido — editar depois do desfecho reescreveria o passado que o
 * Épico 7 soma. `422` quando o corpo traz campo fora do contrato.
 *
 * **A resposta é o Negócio sem dinheiro**, como as demais escritas: o valor mora na ficha, e quem
 * chama a reabre pela invalidação. Reenviar os mesmos valores responde `200` sem gravar nada — nem
 * trilha, nem `atualizado_em` novo.
 *
 * Os campos vão **um a um** pela razão de sempre: o corpo do serviço é fechado.
 */
export function editarNegocio(negocioId: string, edicao: EdicaoDeNegocio): Promise<Negocio> {
  return chamar<Negocio>('negocios', caminhoDoNegocio(negocioId), {
    method: 'PUT',
    corpo: {
      // Corpo completo (11.8): o id do item, ou `null` — "sem previsão".
      previsao_de_fechamento_id: edicao.previsao_de_fechamento_id,
      // Delta (FR-79, 11.3): só vai quando quem chama o mandou — ausente, o serviço não mexe.
      ...(edicao.campos_personalizados === undefined
        ? {}
        : { campos_personalizados: edicao.campos_personalizados }),
    } satisfies EdicaoDeNegocio,
  })
}

/**
 * Registra o dia em que o cliente recebeu ou retirou (`200`), e devolve o Negócio inteiro (6.4).
 *
 * A resposta é o Negócio, e não só a data: é o mesmo corpo das demais escritas, e devolvê-lo é o
 * que deixa a tela reler o que gravou sem uma segunda ida de rede.
 */
export function registrarEntrega(negocioId: string, entregueEm: string): Promise<Negocio> {
  return chamar<Negocio>('negocios', `${caminhoDoNegocio(negocioId)}/entrega`, {
    method: 'PUT',
    corpo: { entregue_em: entregueEm } satisfies RegistroDeEntrega,
  })
}

/**
 * Move o Negócio para outra Etapa do **mesmo** Funil (`200`); Etapa de outro Funil é `409`
 * (11.5). Reenviar a Etapa em que o Negócio já está responde `200` com ele inalterado e **sem** gravar nada — nem linha de movimentação, nem evento, nem trilha.
 *
 * Exige `mover_negocio_de_etapa`. `404` quando a Etapa destino não existe **ou** quando o Negócio
 * não existe ou não é visível; `409` quando ele já está ganho ou perdido.
 */
export function moverDeEtapa(negocioId: string, etapaId: string): Promise<Negocio> {
  return chamar<Negocio>('negocios', `${caminhoDoNegocio(negocioId)}/etapa`, {
    method: 'PUT',
    corpo: { etapa_id: etapaId } satisfies DestinoDoNegocio,
  })
}

/**
 * Troca o Responsável do Negócio (`200`) — a rota própria da 11.5 (AD-20, FR-32, FR-6).
 *
 * Exige `trocar_responsavel`. Num Negócio de **Prospecção**, quando o novo Responsável deriva de
 * Vendedor, o serviço faz o **handoff** automático na mesma transação: a resposta já vem com o
 * `funil_id` e a `etapa_id` do destino (a primeira Etapa do Funil de Vendas). `409` quando o
 * Negócio já fechou ou não há Funil de Vendas; `422` quando o novo Responsável não existe ou está
 * inativo (a mensagem do serviço sai como veio).
 */
export function trocarResponsavel(negocioId: string, responsavelId: string): Promise<Negocio> {
  return chamar<Negocio>('negocios', `${caminhoDoNegocio(negocioId)}/responsavel`, {
    method: 'PUT',
    corpo: { responsavel_id: responsavelId } satisfies TrocaDeResponsavel,
  })
}

/**
 * Troca a Empresa do Negócio (13.5, FR-83) — também ganho ou perdido; o desfecho não muda.
 *
 * Exige `trocar_empresa_do_negocio` (da gestão). `empresaAnteriorId` é a Empresa que a ficha
 * mostrava: se outro gestor já a trocou, o serviço responde `409` em vez de trocar por cima de uma
 * escolha que a pessoa não viu.
 */
export function trocarEmpresa(
  negocioId: string,
  empresaId: string,
  empresaAnteriorId: string,
): Promise<Negocio> {
  return chamar<Negocio>('negocios', `${caminhoDoNegocio(negocioId)}/empresa`, {
    method: 'PUT',
    corpo: {
      empresa_id: empresaId,
      empresa_anterior_id: empresaAnteriorId,
    } satisfies TrocaDeEmpresa,
  })
}

/**
 * Define a **primeira** Empresa do Negócio sem Empresa (`PUT /negocios/{id}/definicao-da-empresa`,
 * 13.20b1) — `200` com o Negócio na Empresa escolhida, em qualquer status.
 *
 * **Só define.** O contato do Negócio é vinculado à Empresa pelo `cadastro`, ao consumir o evento
 * da definição, alguns segundos depois (13.20c): esta tela não pede vínculo nenhum. A Responsável
 * define com `editar_negocio` (uma Empresa dela, do Grupo dela, ou nova); a gestão, com
 * `trocar_empresa_do_negocio`. `409` com o Negócio que já tem outra Empresa ou com a Empresa fora do
 * recorte de quem pede; `404` com a Empresa que não existe ou não chegou a tempo — a mensagem do
 * serviço vai verbatim.
 */
export function definirAEmpresa(negocioId: string, empresaId: string): Promise<Negocio> {
  return chamar<Negocio>('negocios', `${caminhoDoNegocio(negocioId)}/definicao-da-empresa`, {
    method: 'PUT',
    corpo: { empresa_id: empresaId } satisfies DefinicaoDaEmpresa,
  })
}

/**
 * Marca o Negócio como ganho ou perdido (`200`) — **uma** porta para os dois desfechos (AD-13).
 *
 * Exige `marcar_ganho_ou_perdido`. `422` quando a perda vem sem Motivo ou o ganho vem com Motivo;
 * `404` quando o Negócio não existe ou não é visível; `409` quando ele **já** foi fechado — não há
 * rota que o devolva a `em_andamento`, por nenhum caminho (FR-10).
 *
 * O valor congela no ato e **não** aparece na resposta (FR-62). O Negócio fecha onde está: a Etapa
 * não muda.
 */
export function marcarDesfecho(negocioId: string, desfecho: DesfechoDoNegocio): Promise<Negocio> {
  return chamar<Negocio>('negocios', `${caminhoDoNegocio(negocioId)}/desfecho`, {
    method: 'PUT',
    corpo: {
      status: desfecho.status,
      motivo_perda_id: desfecho.motivo_perda_id,
      // A descrição opcional da perda (FR-67 emendado, 11.3); o Motivo continua obrigatório.
      ...(desfecho.descricao_da_perda === undefined
        ? {}
        : { descricao_da_perda: desfecho.descricao_da_perda }),
    } satisfies DesfechoDoNegocio,
  })
}

/**
 * Exclui o Negócio **em andamento** (`204`) — a exclusão é real, e a trilha dele vai junto (FR-11).
 *
 * Não há corpo: a linha some do banco, e não há o que devolver. É o mesmo desenho da exclusão de
 * Funil e de Etapa (FR-68).
 *
 * Exige `excluir_negocio_em_andamento`. `409` quando o Negócio já está ganho ou perdido — o que
 * fechou não sai, nem para o Admin, porque o Ranking do FR-58 e a taxa de conversão vivem dele.
 * `404` quando ele não existe ou não é visível.
 */
export function excluirNegocio(negocioId: string): Promise<void> {
  return chamar<void>('negocios', caminhoDoNegocio(negocioId), { method: 'DELETE' })
}

/**
 * Lê a régua de dias vigente (`200`).
 *
 * Só autenticada: a cor da sinalização aparece para todo perfil, e quem monta o quadro precisa
 * dos cortes tanto quanto quem os escolhe. Numa instalação recém-migrada vêm os de fábrica — 5 e
 * 15, os do FR-12.
 */
export function lerRegua(): Promise<Regua> {
  return chamar<Regua>('negocios', '/regua')
}

/**
 * Lê o painel do dia (`200`): as três faixas da régua e os Negócios mais parados (3.9).
 *
 * Só autenticada, como `GET /funis` e `GET /regua`: quem recorta o que cada um enxerga é a RLS do
 * serviço, linha a linha — em Vendas o Vendedor conta o que é dele, na Prospecção conta tudo
 * (FR-30, FR-31). A tela **não filtra nada** por conta própria, e nem tem como: o que a policy
 * esconde simplesmente não está contado.
 *
 * As três faixas vêm sempre, na ordem, com `quantidade` e `valor_total` — **exceto** para a SDR,
 * cuja resposta não carrega nenhuma chave de valor, em nível nenhum (FR-62).
 *
 * `404` quando a régua não existe (database migrado até a revisão anterior — é a única causa aqui,
 * porque não há Funil a conferir). Carteira vazia é `200` com as três faixas zeradas e
 * `esfriando: []` — "não há nada aqui" é uma resposta, e não um recurso ausente.
 */
export function lerPainelDeHoje(): Promise<PainelDeHoje> {
  return chamar<PainelDeHoje>('negocios', '/hoje')
}

/**
 * Lê a lista de Originados (`200`): os Negócios **pós-handoff**, nos três status (3.11, 11.5).
 *
 * "Pós-handoff" é o fato: há movimentação saindo de um funil de Prospecção. Exige
 * `acessar_originados` desde a 11.5 (o Vendedor de fábrica leva `403`). Escopo `propria` recebe os
 * seus; escopo `todas` recebe todos. Quem recorta é o serviço (a leitura nomeada `originados` do
 * AD-14); a tela **não filtra nada** por conta própria.
 *
 * As linhas vêm do mais recente para o mais antigo, cada uma com `codigo`, `empresa_id`,
 * `responsavel_id` e `responsavel_nome`, `sdr_id` e `sdr_nome`, `status`, as duas datas e
 * `motivo_perda_id` quando houve perda. **Nenhuma chave de dinheiro, em nível nenhum e para perfil
 * nenhum** (FR-62).
 *
 * Quem tem a permissão e nunca originou nada recebe `200` com `[]`. Não há `404` aqui.
 */
export function lerOriginados(): Promise<NegocioOriginado[]> {
  return chamar<NegocioOriginado[]>('negocios', '/originados')
}

/** Os Negócios ganhos sem data de entrega, do ganho mais antigo para o mais recente (6.5). */
export function lerAguardandoEntrega(): Promise<NegocioAguardandoEntrega[]> {
  return chamar<NegocioAguardandoEntrega[]>('negocios', '/aguardando-entrega')
}

/**
 * Grava os cortes novos da régua (`200`) — e **nenhum** Negócio é escrito.
 *
 * A cor é derivada na consulta contra a régua vigente (AD-10): a mesma consulta que devolvia
 * verde passa a devolver vermelho no pedido seguinte, sem recálculo e sem job.
 *
 * Exige `configurar_regua_de_dias`, que o FR-61-A concede a Admin e a Gestor comercial; os demais
 * recebem `403`. `422` quando um dos cortes está fora da faixa aceita ou quando o amarelo passa
 * do vermelho. Reenviar os valores já gravados responde `200` sem gravar nada. Mostre a mensagem
 * do serviço como ela veio.
 *
 * Os dois campos vão **um a um** pela razão de sempre: o corpo do serviço é fechado.
 */
export function configurarRegua(cortes: CortesDaRegua): Promise<Regua> {
  return chamar<Regua>('negocios', '/regua', {
    method: 'PUT',
    corpo: {
      dias_para_amarelo: cortes.dias_para_amarelo,
      dias_para_vermelho: cortes.dias_para_vermelho,
    } satisfies CortesDaRegua,
  })
}

/**
 * O catálogo de Funis e Etapas.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada aqui é o `401` de sessão
 * expirada — que o cliente já renova sozinho, uma vez, antes de deixar o erro subir — e não
 * uma falha que passe sozinha. As três tentativas com espera do padrão só deixariam a tela em
 * "carregando" por segundos antes de dizer o que já se sabia no primeiro milissegundo.
 *
 * O preço é assumido: uma falha transitória de rede ou um `5xx` de passagem vai direto para o
 * ramo de erro, e quem estiver na tela recarrega. A troca é deliberada — a recusa explicada na
 * hora vale mais que a espera muda que talvez se resolvesse.
 *
 * O que ele **não** faz é o que uma leitura apressada sugere: a lista vazia de uma instalação
 * sem Funil nenhum é um `200`, e o react-query nunca repete um `200`.
 */
export function useFunis() {
  return useQuery({ queryKey: CHAVE_DOS_FUNIS, queryFn: lerFunis, retry: false })
}

/**
 * O quadro de um Funil.
 *
 * `enabled` só com Funil escolhido: enquanto o catálogo não chegou não há `funil_id` para
 * mandar, e uma chamada sem ele seria um `422` garantido — a tela mostraria a recusa do
 * validador no lugar do "carregando" que descreve o que está acontecendo.
 *
 * `retry: false` pela mesma razão das demais consultas do CRM: a recusa esperada é o `401` de
 * sessão expirada, que o cliente já renova sozinho uma vez antes de deixar o erro subir.
 */
export function useNegociosDoFunil(funilId: string | undefined) {
  return useQuery({
    queryKey: chaveDoQuadro({ funilId: funilId ?? '' }),
    queryFn: () => listarNegociosDoFunil(funilId!),
    enabled: funilId !== undefined,
    retry: false,
  })
}

/**
 * Os Negócios em andamento de uma Empresa (11.12). `enabled` só com Empresa conhecida: a conversa
 * de Pessoa solta não tem a quem perguntar, e uma chamada sem o parâmetro seria um `422` garantido.
 */
export function useNegociosDaEmpresa(empresaId: string | null | undefined) {
  return useQuery({
    queryKey: chaveDosNegociosDaEmpresa({ empresaId: empresaId ?? '' }),
    queryFn: () => listarNegociosDaEmpresa(empresaId!),
    enabled: empresaId !== null && empresaId !== undefined,
    retry: false,
  })
}

/**
 * Os Negócios em andamento e sem Empresa de um contato (13.20b2). `enabled` só com a Pessoa
 * conhecida: a conversa sem `pessoa_id` não tem a quem perguntar, e o recorte sem o parâmetro seria
 * um `422` garantido.
 */
export function useNegociosDaPessoa(
  pessoaId: string | null | undefined,
  { esperarAte }: { esperarAte?: number } = {},
) {
  return useQuery({
    queryKey: chaveDosNegociosDaPessoa({ pessoaId: pessoaId ?? '' }),
    queryFn: () => listarNegociosDaPessoa(pessoaId!),
    enabled: pessoaId !== null && pessoaId !== undefined,
    retry: false,
    /*
     * **A espera do N0** (revisão da 13.21): a conversa que acabou de nascer de um número novo —
     * pelo "Iniciar nova conversa" ou pela primeira mensagem — tem o Negócio sem Empresa nascendo
     * do outro lado do barramento, segundos depois. Enquanto a lista vier vazia e o prazo
     * (`esperarAte`) não vencer, ela é relida a cada `INTERVALO_DA_ESPERA_DO_N0`; achou, ou venceu,
     * o relógio para. Sem o prazo, nenhum relógio.
     */
    refetchInterval: (consulta) =>
      esperarAte !== undefined &&
      Date.now() < esperarAte &&
      (consulta.state.data === undefined || consulta.state.data.length === 0)
        ? INTERVALO_DA_ESPERA_DO_N0
        : false,
  })
}

/** De quanto em quanto a conversa recém-nascida relê os Negócios do contato (revisão da 13.21). */
export const INTERVALO_DA_ESPERA_DO_N0 = 2_000
/** Por quanto tempo, a partir do nascimento da conversa, ela espera o N0 aparecer. */
export const PRAZO_DA_ESPERA_DO_N0 = 30_000

/**
 * Os Negócios **em andamento** que a pessoa enxerga, em todos os Funis — o vínculo que o diálogo
 * de Tarefa oferece (protótipo v2, "Negócio (opcional — sem negócio a tarefa é avulsa)";
 * 28/09/2026).
 *
 * Não há `GET /negocios` sem recorte: o serviço lista por Funil (o quadro, que só traz os em
 * andamento) ou por Empresa. O caminho é o que o Funil já percorre — o catálogo de Funis e o
 * quadro de cada um, nas **mesmas** chaves de cache, de modo que quem veio do Funil não paga nada
 * de novo. O que a policy não devolveu simplesmente não está aqui (FR-30): o Vendedor vê a
 * própria Carteira, a gestão vê tudo.
 *
 * `habilitada: false` mantém as leituras paradas: o diálogo é montado de dentro de Hoje, e Hoje
 * não pode pedir catálogo nenhum ao montar (DW-299) — só quando o diálogo existir.
 */
export function useNegociosEmAndamento({ habilitada = true }: { habilitada?: boolean } = {}) {
  const funis = useQuery({
    queryKey: CHAVE_DOS_FUNIS,
    queryFn: lerFunis,
    retry: false,
    enabled: habilitada,
  })
  const catalogo = useMemo(() => funis.data ?? [], [funis.data])
  /* `combine` devolve um objeto que o react-query mantém estável enquanto nada muda — é o que
     deixa o `useMemo` abaixo depender dele. */
  const quadros = useQueries({
    queries: catalogo.map((funil) => ({
      queryKey: chaveDoQuadro({ funilId: funil.id }),
      queryFn: () => listarNegociosDoFunil(funil.id),
      retry: false,
      enabled: habilitada,
    })),
    combine: (resultados) => ({
      dados: resultados.map((resultado) => resultado.data),
      pendente: resultados.some((resultado) => resultado.isPending),
      falhou: resultados.some((resultado) => resultado.isError),
    }),
  })
  const negocios = useMemo(
    () =>
      quadros.dados.flatMap((quadro, indice) =>
        (quadro ?? []).map((negocio) => ({
          ...negocio,
          funil_nome: catalogo[indice]?.nome ?? '',
        })),
      ),
    [catalogo, quadros.dados],
  )
  const carregando = habilitada && (funis.isPending || quadros.pendente)
  const falhou = funis.isError || quadros.falhou
  return { negocios, carregando, falhou }
}

/** Um Negócio em andamento como o diálogo de Tarefa o lista: o do quadro, com o nome do Funil. */
export type NegocioEmAndamento = NegocioNoQuadro & { funil_nome: string }

/**
 * As sete mutações compartilham o mesmo desfecho: **reler o catálogo do serviço**.
 *
 * Invalidar, e não remendar o cache com a resposta, é o ponto: a resposta de uma mutação é um
 * Funil (ou uma Etapa), e o que a tela desenha é a lista inteira — com a ordem e as contagens
 * que só o serviço sabe recalcular. Um `setQueryData` com ela apagaria o resto do catálogo.
 *
 * A promessa é **devolvida**: `onSuccess` a aguarda, e é isso que mantém a mutação "pendente"
 * até a lista da tela já refletir o ato.
 */
function useInvalidarOsFunis() {
  const clienteDeConsultas = useQueryClient()
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_FUNIS })
}

/** Cria o Funil e relê o catálogo. */
export function useCriarFunil() {
  const aoTerminar = useInvalidarOsFunis()
  return useMutation({ mutationFn: (novo: NovoFunil) => criarFunil(novo), onSuccess: aoTerminar })
}

/** Grava o nome novo do Funil e relê o catálogo. */
export function useRenomearFunil() {
  const aoTerminar = useInvalidarOsFunis()
  return useMutation({
    mutationFn: ({ funilId, nome }: { funilId: string; nome: string }) =>
      renomearFunil(funilId, nome),
    onSuccess: aoTerminar,
  })
}

/** Exclui o Funil (com as Etapas dele) e relê o catálogo. */
export function useExcluirFunil() {
  const aoTerminar = useInvalidarOsFunis()
  return useMutation({
    mutationFn: (funilId: string) => excluirFunil(funilId),
    onSuccess: aoTerminar,
  })
}

/** Cria a Etapa no fim do Funil e relê o catálogo. */
export function useCriarEtapa() {
  const aoTerminar = useInvalidarOsFunis()
  return useMutation({
    mutationFn: ({ funilId, ...etapa }: { funilId: string } & CorpoDaEtapa) =>
      criarEtapa(funilId, etapa),
    onSuccess: aoTerminar,
  })
}

/** Grava nome e cor da Etapa e relê o catálogo — o quadro pinta a coluna pela cor nova. */
export function useAtualizarEtapa() {
  const aoTerminar = useInvalidarOsFunis()
  return useMutation({
    mutationFn: ({
      funilId,
      etapaId,
      ...etapa
    }: { funilId: string; etapaId: string } & CorpoDaEtapa) =>
      atualizarEtapa(funilId, etapaId, etapa),
    onSuccess: aoTerminar,
  })
}

/** Exclui a Etapa e relê o catálogo. */
export function useExcluirEtapa() {
  const aoTerminar = useInvalidarOsFunis()
  return useMutation({
    mutationFn: ({ funilId, etapaId }: { funilId: string; etapaId: string }) =>
      excluirEtapa(funilId, etapaId),
    onSuccess: aoTerminar,
  })
}

/**
 * O desfecho comum de **toda** escrita de Negócio: reler o catálogo, o quadro **e** o painel.
 *
 * São três chaves porque são três recursos, e os três mudam no mesmo ato: a contagem por Etapa
 * (`quantidade_de_negocios`) vive dentro de `GET /funis`, o card vive em `GET /negocios` e as
 * faixas e a lista do dia vivem em `GET /hoje`. Sem a primeira, a tela de Administração
 * continuaria oferecendo a exclusão de uma Etapa que acabou de deixar de estar vazia; sem a
 * segunda, criar um Negócio e não o ver aparecer no quadro seria o primeiro defeito relatado.
 *
 * **A terceira nasceu com a tela Hoje (3.9)**, e ela é a mais fácil de esquecer justamente porque
 * a escrita acontece em `/funil` e o efeito aparece em `/hoje`: um Negócio novo é uma contagem a
 * mais numa faixa (a verde, porque ele nasce sem contato e com a data de início de hoje) e pode
 * ser uma linha a mais na lista. Sem ela, a **primeira tela depois do login** continuaria
 * mostrando o dia de antes do último cadastro.
 *
 * O quadro é invalidado pelo **prefixo**, e não por uma chave: quem escreve pode ter mudado o
 * quadro de qualquer Funil — um handoff (3.3) troca de Funil por definição —, e invalidar só o
 * que está aberto deixaria o outro desatualizado até alguém recarregar. O painel não precisa de
 * prefixo: ele não é de Funil nenhum, e a chave é uma só.
 *
 * **Continuam sendo três chamadas depois da 3.10, e duas delas passaram a alcançar mais coisa.**
 * `chaveDaFicha` e `chaveDasMovimentacoes` são construídas **sob** `CHAVE_DOS_NEGOCIOS`
 * justamente para isto: invalidar aquele **prefixo** alcança o quadro de todo Funil **e** a ficha
 * e a trilha de todo Negócio, sem uma linha a mais aqui. Mover troca a Etapa da ficha e acrescenta
 * uma linha ao histórico; fechar troca o `status` e a `data_de_conclusao`; editar troca os três
 * campos e o `atualizado_em` — e as três acontecem **na própria ficha**, que é a tela que mentiria
 * primeiro.
 *
 * O preço do prefixo está declarado: ele invalida também a ficha dos Negócios que a tela não está
 * mostrando. Isso não custa rede — o react-query só refaz busca de consulta **montada** —, e a
 * alternativa (invalidar a chave exata do Negócio escrito) deixaria de fora o caso em que a
 * escrita muda outro: nenhum ato de hoje faz isso, e o dia em que fizer não avisaria.
 *
 * **E desde a 3.11 são quatro chamadas, porque `criar_negocio` é o único escritor de `sdr_id`
 * (FR-6).** Quem se nomeia SDR no formulário de Negócio novo é, na prática, a própria SDR — e a
 * lista de Originados dela acabou de ganhar uma linha. `CHAVE_DOS_ORIGINADOS` é de recurso
 * próprio, fora do prefixo do quadro, e sem esta linha o `staleTime` global de 30 segundos
 * (`api/consultas.ts`) faria a tela seguinte mostrar a lista de antes do cadastro.
 *
 * As três irmãs de escrita (mover, fechar, editar) não mudam `sdr_id` — a varredura AST do serviço
 * reprova qualquer segundo escritor —, mas mudam o `status` e a `data_de_conclusao`, que são
 * **colunas da lista**: invalidar para as quatro é o que mantém a regra simples, e o custo é zero
 * quando a tela não está montada (o react-query só refaz busca de consulta montada).
 *
 * A promessa é **devolvida**: `onSuccess` aguarda as quatro, e é isso que mantém a mutação
 * "pendente" até a tela já refletir o ato.
 */
function useInvalidarOQuadroEOCatalogo() {
  const clienteDeConsultas = useQueryClient()
  return () =>
    Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_FUNIS }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_NEGOCIOS }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_PAINEL_DE_HOJE }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_ORIGINADOS }),
      //: A lista da 6.5 entra na mesma invalidação: registrar a entrega tira uma linha dela, e
      //: ganhar um Negócio põe outra. Ela é a única tela cujo conteúdo **some** com a escrita.
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_AGUARDANDO_ENTREGA }),
      //: 13.14b: o lote das Empresas (a ficha do Grupo e o aviso de CNPJ) — com o modal aberto
      //: sobre elas, ganhar, perder, mover ou excluir não pode deixar a soma nem o link velhos.
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_NEGOCIOS_DAS_EMPRESAS }),
    ])
}

/** O prefixo do lote de Negócios por Empresas (13.9) — o que a ficha do Grupo e o aviso leem. */
export const CHAVE_DOS_NEGOCIOS_DAS_EMPRESAS = ['negocios', 'das-empresas'] as const

/**
 * Quando a ficha da Empresa é relida depois de excluir um Negócio dela (13.14b, D-4): a marca
 * "recebeu orçamento" sai no `cadastro` quando ele consome o `negocio_excluido` (dois saltos de
 * trabalhador), e não no `204` desta rota.
 */
export const RELEITURAS_DA_FICHA_DEPOIS_DA_EXCLUSAO_EM_MS = [2000, 4000, 8000, 16000] as const

/**
 * Cria o Negócio e relê o catálogo **e** o quadro.
 *
 * Ela foi a **primeira** mutação de Negócio deste cliente, e desde a 3.10 tem quatro irmãs —
 * mover, fechar, editar e excluir. Todas compartilham este mesmo desfecho, e é para isso que
 * `useInvalidarOQuadroEOCatalogo` é uma função de módulo e não um trecho dentro deste hook.
 */
export function useCriarNegocio() {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: (negocio: NovoNegocio) => criarNegocio(negocio),
    onSuccess: aoTerminar,
  })
}

/**
 * A ficha de um Negócio.
 *
 * `retry: false` pela mesma razão das demais consultas do CRM, e aqui ela pesa mais: a recusa
 * esperada é o **`404`** — o Negócio não existe, ou a RLS não o entrega —, e nenhuma das duas muda
 * por insistir. As três tentativas com espera do padrão deixariam a tela em "carregando" por
 * segundos antes de dizer o que já se sabia no primeiro milissegundo.
 *
 * **`habilitada` existe por um caso só, e ele é o da exclusão.** Removida do cache, uma consulta
 * cujo observador continua **montado** é recriada e buscada de novo pelo react-query — e o `GET`
 * seguinte ao `DELETE` recebe o `404` do Negócio que acabou de sumir, que a tela desenharia como
 * recusa no caminho de saída. Desligá-la enquanto a navegação de volta acontece é o que faz o
 * `removeQueries` de `useExcluirNegocio` valer de verdade. Fora desse instante ela é sempre `true`.
 */
export function useFicha(negocioId: string, { habilitada = true } = {}) {
  return useQuery({
    queryKey: chaveDaFicha({ negocioId }),
    queryFn: () => lerFicha(negocioId),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * A trilha de movimentações de um Negócio.
 *
 * `retry: false` pela mesma razão da ficha, e `habilitada` pelo mesmo caso: excluído o Negócio, a
 * trilha dele morre junto (a cascata da FK), e um refetch receberia o mesmo `404`.
 */
export function useMovimentacoes(negocioId: string, { habilitada = true } = {}) {
  return useQuery({
    queryKey: chaveDasMovimentacoes({ negocioId }),
    queryFn: () => listarMovimentacoes(negocioId),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Grava os três campos do FR-13 e relê tudo o que o ato mudou.
 *
 * O `negocioId` vem do **hook**, e não da chamada: quem edita é a ficha daquele Negócio, e passá-lo
 * a cada `mutate` abriria a chance de a tela gravar num Negócio e invalidar outro.
 */
export function useEditarNegocio(negocioId: string) {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: (edicao: EdicaoDeNegocio) => editarNegocio(negocioId, edicao),
    onSuccess: aoTerminar,
  })
}

/**
 * Move o Negócio de Etapa **dentro do mesmo Funil** (FR-5). Desde a 11.5 Etapa de outro Funil é
 * `409`: o handoff é efeito da troca de Responsável (`useTrocarResponsavel`).
 */
export function useMoverDeEtapa(negocioId: string) {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: (etapaId: string) => moverDeEtapa(negocioId, etapaId),
    onSuccess: aoTerminar,
  })
}

/**
 * Troca o Responsável do Negócio (11.5) — e é esta, desde a 11.5, a mutação do **handoff**.
 *
 * O desfecho é o de toda escrita de Negócio: relê quadro, ficha, trilha, catálogo, painel e
 * Originados — o Negócio entregue some do Funil da SDR e aparece em Originados.
 */
export function useTrocarResponsavel(negocioId: string) {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: (responsavelId: string) => trocarResponsavel(negocioId, responsavelId),
    onSuccess: aoTerminar,
  })
}

/**
 * Troca a Empresa do Negócio (13.5): relê quadro, ficha e listas, e as duas fichas de Empresa (a
 * antiga perde o Negócio; a nova o ganha). A ida da nova ao Carga Máquina e a marca da antiga vêm
 * depois, pelos eventos — a releitura imediata pode ainda não as mostrar.
 */
export function useTrocarEmpresa(negocioId: string) {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ empresaId, empresaAnteriorId }: { empresaId: string; empresaAnteriorId: string }) =>
      trocarEmpresa(negocioId, empresaId, empresaAnteriorId),
    onSuccess: async () => {
      await Promise.all([
        aoTerminar(),
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresa'] }),
        //: A lista de Empresas. A ida ao Carga Máquina é assíncrona (o evento passa pelo
        //: `cadastro`): ela aparece na próxima leitura.
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresas'] }),
      ])
    },
  })
}

/**
 * Define a Empresa do Negócio sem Empresa (13.20b2): relê quadro, ficha, listas e os recortes por
 * Pessoa (o Negócio sai do "sem Empresa"), e as fichas de Empresa (a escolhida ganha o Negócio). O
 * vínculo do contato vem depois, pelo `cadastro` — quem espera por ele é `useEsperaDoVinculo`.
 */
export function useDefinirAEmpresa(negocioId: string) {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (empresaId: string) => definirAEmpresa(negocioId, empresaId),
    /* As recusas que dizem algo sobre o Negócio (code review da 13.20b2, B5) — `403` (deixou de ser
       de quem age), `404` (sumiu, ou a Empresa não chegou) e `409` (já tem Empresa, ou fora do
       recorte) — relêem as leituras de Negócio: a ficha e os recortes param de mostrar o antigo. */
    onError: async (falha) => {
      if (falha instanceof ErroDaApi && [403, 404, 409].includes(falha.status)) {
        await clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_NEGOCIOS })
      }
    },
    onSuccess: async () => {
      await Promise.all([
        aoTerminar(),
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresa'] }),
        clienteDeConsultas.invalidateQueries({ queryKey: ['cadastro', 'empresas'] }),
      ])
    },
  })
}

/**
 * Move um card do quadro de Etapa — o **arrastar** do Funil (11.4). A mesma rota de sempre
 * (`PUT /negocios/{id}/etapa`); o que muda é o desenho **otimista**: o card troca de coluna no
 * cache do quadro antes da resposta. Na recusa volta **só o card que falhou** — e só se ele ainda
 * estiver onde o gesto o pôs —, e não o quadro inteiro de antes: dois arrastos em voo, um recusado,
 * não desfazem o outro. Em qualquer desfecho o quadro é relido (`onSettled`); no sucesso, a
 * invalidação de sempre relê também ficha, catálogo e painel.
 */
export function useMoverCardDeEtapa(funilId: string | undefined) {
  const clienteDeConsultas = useQueryClient()
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: ({ negocioId, etapaId }: { negocioId: string; etapaId: string }) =>
      moverDeEtapa(negocioId, etapaId),
    onMutate: async ({ negocioId, etapaId }) => {
      if (funilId === undefined) {
        return { etapaAnterior: undefined }
      }
      const chave = chaveDoQuadro({ funilId })
      await clienteDeConsultas.cancelQueries({ queryKey: chave })
      const anterior = clienteDeConsultas.getQueryData<NegocioNoQuadro[]>(chave)
      const etapaAnterior = anterior?.find((card) => card.id === negocioId)?.etapa_id
      if (anterior !== undefined) {
        clienteDeConsultas.setQueryData<NegocioNoQuadro[]>(
          chave,
          anterior.map((card) => (card.id === negocioId ? { ...card, etapa_id: etapaId } : card)),
        )
      }
      return { etapaAnterior }
    },
    onError: (_falha, { negocioId, etapaId }, contexto) => {
      const etapaAnterior = contexto?.etapaAnterior
      if (funilId === undefined || etapaAnterior === undefined) {
        return
      }
      clienteDeConsultas.setQueryData<NegocioNoQuadro[]>(chaveDoQuadro({ funilId }), (agora) =>
        agora?.map((card) =>
          card.id === negocioId && card.etapa_id === etapaId
            ? { ...card, etapa_id: etapaAnterior }
            : card,
        ),
      )
    },
    onSuccess: aoTerminar,
    onSettled: () =>
      funilId === undefined
        ? undefined
        : clienteDeConsultas.invalidateQueries({ queryKey: chaveDoQuadro({ funilId }) }),
  })
}

/**
 * Registra a data de entrega e relê o que ela muda (6.4, FR-74).
 *
 * A invalidação é a mesma das outras escritas do Negócio: a ficha mostra a data, e a RAT ao lado
 * dela mostra a indicação de garantia derivada dela — as duas precisam reler.
 */
export function useRegistrarEntrega(negocioId: string) {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: (entregueEm: string) => registrarEntrega(negocioId, entregueEm),
    onSuccess: aoTerminar,
  })
}

/** Marca o desfecho (ganho ou perdido) e relê tudo o que o ato mudou. */
export function useMarcarDesfecho(negocioId: string) {
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: (desfecho: DesfechoDoNegocio) => marcarDesfecho(negocioId, desfecho),
    onSuccess: aoTerminar,
  })
}

/**
 * Exclui o Negócio: **remove** a ficha e a trilha dele do cache, e relê o resto.
 *
 * **`removeQueries`, e não `invalidateQueries`, e a diferença é visível na tela.** Invalidar refaz
 * a busca das consultas **montadas** — e no instante da exclusão a ficha daquele Negócio ainda
 * está montada, porque o `onSuccess` deste hook é aguardado **antes** do `onSuccess` do `mutate`
 * que navega de volta. O `GET` refeito recebe o `404` do Negócio que acabou de sumir, e a tela
 * pisca "Negócio não encontrado" antes de a navegação acontecer. A linha morreu; o cache dela não
 * deve ser refeito, deve **sumir**.
 *
 * As demais chaves continuam sendo invalidadas: o quadro perdeu um card, o catálogo perdeu uma
 * unidade na contagem por Etapa e o painel do dia perdeu uma contagem de faixa — as três descrevem
 * coisas que continuam existindo, e por isso são relidas, e não removidas.
 */
export function useExcluirNegocio(negocioId: string) {
  const clienteDeConsultas = useQueryClient()
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  //: A Empresa do Negócio (13.14b) viaja na chamada (`mutate(empresaId)`), e não no hook: a ficha
  //: que exclui só a conhece depois de lida. Opcional (`void`): quem não a tem chama sem nada.
  return useMutation<Awaited<ReturnType<typeof excluirNegocio>>, Error, string | void>({
    mutationFn: () => excluirNegocio(negocioId),
    onSuccess: async (_corpo, empresaId) => {
      clienteDeConsultas.removeQueries({ queryKey: chaveDaFicha({ negocioId }) })
      clienteDeConsultas.removeQueries({ queryKey: chaveDasMovimentacoes({ negocioId }) })
      //: 13.14b (DW-402, D-4): a ficha da Empresa relida agora e em 2, 4, 8 e 16 s — o aviso de CNPJ
      //: some quando o `cadastro` tira a marca. Agendado pelo cliente de consultas, e não por um
      //: efeito: o modal que excluiu já fechou quando o relógio dispara.
      if (typeof empresaId === 'string') {
        const chave = chaveDaEmpresa(empresaId)
        void clienteDeConsultas.invalidateQueries({ queryKey: chave })
        for (const espera of RELEITURAS_DA_FICHA_DEPOIS_DA_EXCLUSAO_EM_MS) {
          setTimeout(() => {
            void clienteDeConsultas.invalidateQueries({ queryKey: chave })
          }, espera)
        }
      }
      await aoTerminar()
    },
  })
}

/**
 * A régua de dias vigente.
 *
 * `retry: false` pela mesma razão das demais consultas do CRM: a recusa esperada é o `401` de
 * sessão expirada — que o cliente já renova sozinho, uma vez, antes de deixar o erro subir.
 */
export function useRegua() {
  return useQuery({ queryKey: CHAVE_DA_REGUA, queryFn: lerRegua, retry: false })
}

/**
 * O painel do dia.
 *
 * `retry: false` pela mesma razão das demais consultas do CRM: a recusa esperada é o `401` de
 * sessão expirada — que o cliente já renova sozinho, uma vez, antes de deixar o erro subir. As
 * três tentativas com espera do padrão só deixariam a **primeira tela depois do login** em
 * "carregando" por segundos antes de dizer o que já se sabia no primeiro milissegundo.
 *
 * Sem `enabled`: ao contrário do quadro, esta leitura não depende de nada que o catálogo traga —
 * ela não tem parâmetro. O catálogo é lido ao lado só para resolver o Funil de cada item da lista.
 */
export function usePainelDeHoje() {
  return useQuery({ queryKey: CHAVE_DO_PAINEL_DE_HOJE, queryFn: lerPainelDeHoje, retry: false })
}

/**
 * A lista de Originados da SDR.
 *
 * `retry: false` pela mesma razão das demais consultas do CRM: a recusa esperada é o `401` de
 * sessão expirada — que o cliente já renova sozinho, uma vez, antes de deixar o erro subir. As três
 * tentativas com espera do padrão só deixariam a tela em "carregando" por segundos antes de dizer
 * o que já se sabia no primeiro milissegundo.
 *
 * Sem `enabled`: esta leitura não tem parâmetro nenhum, e não depende do catálogo nem da régua —
 * a lista não desenha Etapa e não tem cor. É a mesma forma de `usePainelDeHoje`.
 *
 * **E `useInvalidarOQuadroEOCatalogo` a invalida.** A tela é leitura pura, mas a chave não é
 * inalcançável a partir do SPA: `criar_negocio` é o **único** escritor de `sdr_id` (FR-6), e quem
 * preenche o campo SDR com o próprio nome, em `/funil`, é justamente quem vai abrir esta lista em
 * seguida. Sem a invalidação, o `staleTime` global de 30 segundos (`api/consultas.ts`) serviria a
 * lista de antes do cadastro que a pessoa acabou de fazer. Fechar, mover e editar entram pela
 * mesma porta: eles não tocam `sdr_id`, mas trocam `status` e `data_de_conclusao`, que são colunas
 * desta lista.
 *
 * O que continua fora do alcance é a escrita de **outra sessão** — o Vendedor que fecha o Negócio
 * entregue —, e para essa não há invalidação possível: quem quer o estado novo recarrega.
 */
export function useOriginados() {
  return useQuery({ queryKey: CHAVE_DOS_ORIGINADOS, queryFn: lerOriginados, retry: false })
}

/**
 * A lista "Aguardando entrega" (6.5, FR-75).
 *
 * `retry: false` como as irmãs: uma recusa do serviço é para ser mostrada, e não repetida três
 * vezes antes de aparecer.
 */
export function useAguardandoEntrega() {
  return useQuery({
    queryKey: CHAVE_DO_AGUARDANDO_ENTREGA,
    queryFn: lerAguardandoEntrega,
    retry: false,
  })
}

/**
 * Grava os cortes novos e relê a régua do serviço.
 *
 * Invalidar, e não remendar o cache com a resposta, é o ponto — e aqui ele tem um motivo a mais
 * que nas mutações de Funil: o serviço tem um **ramo idempotente**, e reenviar os valores já
 * gravados devolve a régua com o `atualizado_em` antigo. Um `setQueryData` com a resposta
 * funcionaria; a invalidação é o que mantém o cartão mostrando o que o serviço tem, e não o que
 * a tela supôs.
 *
 * `CHAVE_DOS_FUNIS` **não** é invalidada: mudar a régua não move Negócio nenhum e não muda
 * contagem nenhuma — é exatamente essa a propriedade que o serviço promete (AD-10).
 *
 * **`CHAVE_DOS_NEGOCIOS` é**, e a diferença nasceu com o quadro (3.8). A `cor` de cada card não
 * está gravada: ela é **derivada na consulta**, contra os cortes que a régua tinha no instante em
 * que aquele `GET /negocios` foi respondido (AD-10). Cortes novos, portanto, mudam a resposta do
 * quadro sem mudar Negócio nenhum — e sem esta linha o cache serviria as cores da régua velha
 * enquanto a legenda, que vem de `CHAVE_DA_REGUA`, já mostraria a nova: duas réguas na mesma
 * tela, e a que pinta os cards seria a que ninguém está lendo.
 *
 * **`CHAVE_DO_PAINEL_DE_HOJE` também é, e ali o efeito é maior que uma repintura.** No painel a
 * régua decide a **contagem** e a **soma** de cada faixa, e não só a cor: apertar os cortes move
 * Negócios de faixa, e com eles o KPI "No vermelho" e a nota que diz "acima de N dias sem
 * contato". Sem esta linha, a gestão mudaria a régua em `/configuracao` e a tela do dia
 * continuaria contando pela régua antiga — com a legenda já dizendo a nova ao lado.
 */
export function useConfigurarRegua() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (cortes: CortesDaRegua) => configurarRegua(cortes),
    onSuccess: () =>
      Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_REGUA }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_NEGOCIOS }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_PAINEL_DE_HOJE }),
      ]),
  })
}

/** Reescreve a ordem das Etapas e relê o catálogo. */
export function useReordenarEtapas() {
  const aoTerminar = useInvalidarOsFunis()
  return useMutation({
    mutationFn: ({ funilId, etapas }: { funilId: string; etapas: readonly string[] }) =>
      reordenarEtapas(funilId, etapas),
    onSuccess: aoTerminar,
  })
}

/* --- O catálogo de Tipos de Tarefa e Resultados (história 4.1) --------------------- */

/**
 * Um Tipo de Tarefa com os Resultados dele, na ordem.
 *
 * Os Resultados vêm **dentro** do Tipo, e não numa rota própria: quem lê este catálogo — o
 * seletor de Tipo da Tarefa, o de Resultado da conclusão, a tela de Administração — sempre
 * precisa dos dois juntos, e uma segunda chamada por Tipo seria seis idas à rede para desenhar
 * um seletor.
 */
export type TipoDeTarefa = Esquemas['RespostaDeTipoDeTarefa']

/** Uma opção de Resultado daquele Tipo: nome, posição e se ela ainda está em circulação. */
export type ResultadoDeTarefa = Esquemas['RespostaDeResultadoDeTarefa']

/**
 * O que a tela envia para criar ou renomear um Tipo: o nome, e só.
 *
 * Ele existe para ser o `satisfies` dos dois corpos — é ele, e não a boa vontade de quem chama,
 * que garante que nada além de `nome` chegue ao serviço, e que um `{ nom: ... }` não compile.
 */
export type NomeDeTipoDeTarefa = Esquemas['CorpoDeNomeDeTipoDeTarefa']

/** O marcador id-estável do Tipo (11.6): o que os relatórios contam por ele — nunca o nome. */
export type ContaComo = Esquemas['ContaComo']

/** O ícone e o marcador do Tipo (11.6) — corpo completo: nulo apaga. */
export type ConfiguracaoDoTipo = Esquemas['CorpoDaConfiguracaoDoTipo']

/** A sugestão de próximo passo daquele Resultado (FR-21) — o par, ou nulo nos dois. */
export type SugestaoPorResultado = Esquemas['CorpoDaSugestao']

/** O mesmo para o Resultado — tipo próprio porque o schema do serviço é outro. */
export type NomeDeResultado = Esquemas['CorpoDeNomeDeResultado']

/**
 * Chave de cache do catálogo: uma só, porque a resposta traz os Tipos e os Resultados juntos.
 *
 * Ela é **própria**, e não a do catálogo de Funis: são dois recursos, com guardas diferentes
 * (`gerir_funis_e_etapas` e `gerir_tipos_de_tarefa`) e regras de exclusão opostas — Funil some de
 * verdade (FR-68), Tipo fica com `ativo: false` (FR-69). Compartilhar a chave faria renomear uma
 * Etapa reler o vocabulário de Tarefa, e criar um Tipo reler a estrutura de trabalho inteira.
 *
 * É a mesma que o seletor de Tipo da Tarefa vai ler quando ele chegar (4.2): a tela de
 * Administração e o formulário de Tarefa consultam o **mesmo** catálogo, e duas chaves fariam um
 * mostrar o Tipo que o outro acabou de excluir.
 */
export const CHAVE_DOS_TIPOS_DE_TAREFA = ['negocios', 'tipos-de-tarefa'] as const

function caminhoDoTipoDeTarefa(tipoId: string): string {
  return `/tipos-de-tarefa/${encodeURIComponent(tipoId)}`
}

function caminhoDoResultado(tipoId: string, resultadoId: string): string {
  return `${caminhoDoTipoDeTarefa(tipoId)}/resultados/${encodeURIComponent(resultadoId)}`
}

/**
 * Lê os Tipos com os Resultados de cada um (`200`).
 *
 * Só autenticada: o catálogo é o seletor de Tipo da Tarefa, e ele aparece para todo perfil. Numa
 * instalação recém-migrada vêm os seis Tipos de fábrica do FR-15, com os Resultados de cada um.
 *
 * O que foi excluído **não** vem: a exclusão é lógica, e esta é a leitura das Tarefas **novas**
 * (FR-69). A Tarefa antiga que aponta para um Tipo excluído continua exibindo o rótulo dele — e
 * como isso chega à tela é decisão da 4.2, que é quem traz a Tarefa.
 */
export function lerTiposDeTarefa(): Promise<TipoDeTarefa[]> {
  return chamar<TipoDeTarefa[]>('negocios', '/tipos-de-tarefa')
}

/**
 * O desfecho de `cadastrarTipoDeTarefa`: o Tipo, e **como** ele veio a existir.
 *
 * `restaurado` é `true` quando o nome existia excluído e o serviço reativou a linha — mesmo `id`,
 * e portanto toda Tarefa que já apontava para ela volta a apontar para um Tipo vivo, com os
 * Resultados que ele tinha. Nada nasceu, e dizer "criado" ali seria contar a história errada.
 */
export interface TipoDeTarefaCadastrado {
  tipo: TipoDeTarefa
  restaurado: boolean
}

/** O mesmo par, um nível abaixo: o Resultado, e se ele nasceu ou voltou. */
export interface ResultadoCadastrado {
  resultado: ResultadoDeTarefa
  restaurado: boolean
}

/**
 * Cadastra o Tipo (`201`) — **ou reativa** o que existia excluído (`200`, mesmo `id`).
 *
 * Exige `gerir_tipos_de_tarefa`, que o FR-61-A concede **só ao Admin** — nem o Gestor comercial
 * passa. `409` quando o nome já existe **ativo**, `422` quando ele vem vazio ou longo demais.
 *
 * O status é lido, e não descartado: é ele — e só ele — que separa os dois desfechos, porque o
 * corpo dos dois é idêntico. O serviço declara os dois no schema, então isto não é adivinhação
 * sobre um comportamento não documentado.
 */
export async function cadastrarTipoDeTarefa(nome: string): Promise<TipoDeTarefaCadastrado> {
  const { corpo, status } = await chamarComStatus<TipoDeTarefa>('negocios', '/tipos-de-tarefa', {
    method: 'POST',
    corpo: { nome } satisfies NomeDeTipoDeTarefa,
  })
  return { tipo: corpo, restaurado: status === 200 }
}

/**
 * Troca o nome do Tipo (`200`). As Tarefas que apontam para ele passam a exibi-lo.
 *
 * **Reenviar o nome que já está gravado não deixa registro na trilha** — o serviço compara antes
 * de gravar. `404` quando o `id` não existe; `409` quando o nome novo já é de outro Tipo.
 */
export function renomearTipoDeTarefa(tipoId: string, nome: string): Promise<TipoDeTarefa> {
  return chamar<TipoDeTarefa>('negocios', caminhoDoTipoDeTarefa(tipoId), {
    method: 'PUT',
    corpo: { nome } satisfies NomeDeTipoDeTarefa,
  })
}

/**
 * Exclui o Tipo (`200` com `ativo: false`) — exclusão **lógica** (FR-69).
 *
 * O Tipo some da criação de Tarefas novas e continua legível nas antigas: a linha dele e as dos
 * Resultados dele ficam no banco. Por isso a resposta tem corpo, e não é um `204`: o corpo é a
 * prova de que nada foi apagado — o oposto do `DELETE` de Funil e de Etapa.
 *
 * `404` quando o `id` não existe **ou** quando o Tipo já está excluído.
 */
export function excluirTipoDeTarefa(tipoId: string): Promise<TipoDeTarefa> {
  return chamar<TipoDeTarefa>('negocios', caminhoDoTipoDeTarefa(tipoId), { method: 'DELETE' })
}

/**
 * Adiciona um Resultado ao Tipo (`201`) — **ou reativa** o que existia removido (`200`).
 *
 * `404` quando o Tipo não existe; `409` quando o nome já é de outro Resultado **deste** Tipo — a
 * mesma "Realizada" em Reunião e em Visita é aceita, e a semente do FR-15 tem exatamente isso.
 */
export async function cadastrarResultado(
  tipoId: string,
  nome: string,
): Promise<ResultadoCadastrado> {
  const { corpo, status } = await chamarComStatus<ResultadoDeTarefa>(
    'negocios',
    `${caminhoDoTipoDeTarefa(tipoId)}/resultados`,
    { method: 'POST', corpo: { nome } satisfies NomeDeResultado },
  )
  return { resultado: corpo, restaurado: status === 200 }
}

/**
 * Troca o nome do Resultado (`200`). A linha é a mesma, e as Tarefas concluídas não se mexem.
 *
 * É essa a diferença entre renomear e "remover e criar outro". `404` quando o Resultado não
 * existe **ou** é de outro Tipo; `409` quando o nome novo colide neste Tipo.
 */
export function renomearResultado(
  tipoId: string,
  resultadoId: string,
  nome: string,
): Promise<ResultadoDeTarefa> {
  return chamar<ResultadoDeTarefa>('negocios', caminhoDoResultado(tipoId, resultadoId), {
    method: 'PUT',
    corpo: { nome } satisfies NomeDeResultado,
  })
}

/**
 * Remove o Resultado (`200` com `ativo: false`) — remoção **lógica** (FR-16).
 *
 * Ele some da listagem daquele Tipo e continua gravado nas Tarefas concluídas que o escolheram.
 * `404` quando o Resultado não existe, é de outro Tipo, ou já está removido.
 */
export function removerResultado(tipoId: string, resultadoId: string): Promise<ResultadoDeTarefa> {
  return chamar<ResultadoDeTarefa>('negocios', caminhoDoResultado(tipoId, resultadoId), {
    method: 'DELETE',
  })
}

/**
 * Grava o ícone (emoji) e o marcador `conta_como` do Tipo (11.6, `gerir_tipos_de_tarefa`).
 */
export function configurarTipoDeTarefa(
  tipoId: string,
  configuracao: ConfiguracaoDoTipo,
): Promise<TipoDeTarefa> {
  return chamar<TipoDeTarefa>('negocios', `${caminhoDoTipoDeTarefa(tipoId)}/configuracao`, {
    method: 'PUT',
    corpo: configuracao,
  })
}

/** Grava o marcador de êxito do Resultado (11.6, `gerir_tipos_de_tarefa`). */
export function configurarResultado(
  tipoId: string,
  resultadoId: string,
  contaComoExito: boolean,
): Promise<ResultadoDeTarefa> {
  return chamar<ResultadoDeTarefa>(
    'negocios',
    `${caminhoDoResultado(tipoId, resultadoId)}/configuracao`,
    { method: 'PUT', corpo: { conta_como_exito: contaComoExito } },
  )
}

/**
 * Grava a sugestão de próximo passo do Resultado (FR-21, `configurar_sugestao_por_resultado`).
 * Nulo nos dois apaga: volta a regra padrão (mesmo tipo, 3 dias).
 */
export function configurarSugestao(
  tipoId: string,
  resultadoId: string,
  sugestao: SugestaoPorResultado,
): Promise<ResultadoDeTarefa> {
  return chamar<ResultadoDeTarefa>(
    'negocios',
    `${caminhoDoResultado(tipoId, resultadoId)}/sugestao`,
    { method: 'PUT', corpo: sugestao },
  )
}

/**
 * Lê o catálogo de Tipos de Tarefa.
 *
 * `retry: false` pela mesma razão das demais consultas do CRM: a recusa esperada é o `401` de
 * sessão expirada — que o cliente já renova sozinho, uma vez, antes de deixar o erro subir. As
 * três tentativas com espera do padrão só deixariam a tela em "carregando" por segundos antes de
 * dizer o que já se sabia no primeiro milissegundo.
 */
export function useTiposDeTarefa() {
  return useQuery({
    queryKey: CHAVE_DOS_TIPOS_DE_TAREFA,
    queryFn: lerTiposDeTarefa,
    retry: false,
  })
}

/**
 * O desfecho comum das seis mutações: **reler o catálogo do serviço**.
 *
 * Invalidar, e não remendar o cache com a resposta, é o ponto: a resposta de uma mutação é **um**
 * Tipo (ou um Resultado), e o que a tela desenha é a lista inteira. Um `setQueryData` com ela
 * apagaria o resto do catálogo — e, no caso do `DELETE`, escreveria no cache uma linha com
 * `ativo: false` que a leitura seguinte não devolve.
 *
 * `CHAVE_DOS_FUNIS` **não** é invalidada: Tipo de Tarefa e Funil são dois catálogos diferentes, e
 * mexer num não move nada no outro.
 */
function useInvalidarOsTiposDeTarefa() {
  const clienteDeConsultas = useQueryClient()
  // A promessa continua sendo devolvida: `onSuccess` a aguarda, e é isso que mantém a mutação
  // "pendente" até a lista da tela já refletir o ato.
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_TIPOS_DE_TAREFA })
}

/** Cadastra (ou reativa) o Tipo e relê o catálogo. */
export function useCadastrarTipoDeTarefa() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: (nome: string) => cadastrarTipoDeTarefa(nome),
    onSuccess: aoTerminar,
  })
}

/** Grava o nome novo do Tipo e relê o catálogo. */
export function useRenomearTipoDeTarefa() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: ({ tipoId, nome }: { tipoId: string; nome: string }) =>
      renomearTipoDeTarefa(tipoId, nome),
    onSuccess: aoTerminar,
  })
}

/** Exclui logicamente o Tipo e relê o catálogo — ele some das Tarefas novas. */
export function useExcluirTipoDeTarefa() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: (tipoId: string) => excluirTipoDeTarefa(tipoId),
    onSuccess: aoTerminar,
  })
}

/** Adiciona (ou reativa) o Resultado e relê o catálogo. */
export function useCadastrarResultado() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: ({ tipoId, nome }: { tipoId: string; nome: string }) =>
      cadastrarResultado(tipoId, nome),
    onSuccess: aoTerminar,
  })
}

/** Grava o nome novo do Resultado e relê o catálogo. */
export function useRenomearResultado() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: ({
      tipoId,
      resultadoId,
      nome,
    }: {
      tipoId: string
      resultadoId: string
      nome: string
    }) => renomearResultado(tipoId, resultadoId, nome),
    onSuccess: aoTerminar,
  })
}

/** Remove logicamente o Resultado e relê o catálogo. */
export function useRemoverResultado() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: ({ tipoId, resultadoId }: { tipoId: string; resultadoId: string }) =>
      removerResultado(tipoId, resultadoId),
    onSuccess: aoTerminar,
  })
}

/** Grava o ícone e o marcador do Tipo e relê o catálogo (11.6). */
export function useConfigurarTipoDeTarefa() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: ({ tipoId, configuracao }: { tipoId: string; configuracao: ConfiguracaoDoTipo }) =>
      configurarTipoDeTarefa(tipoId, configuracao),
    onSuccess: aoTerminar,
  })
}

/** Grava o marcador de êxito do Resultado e relê o catálogo (11.6). */
export function useConfigurarResultado() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: ({
      tipoId,
      resultadoId,
      contaComoExito,
    }: {
      tipoId: string
      resultadoId: string
      contaComoExito: boolean
    }) => configurarResultado(tipoId, resultadoId, contaComoExito),
    onSuccess: aoTerminar,
  })
}

/** Grava a sugestão por Resultado e relê o catálogo (FR-21). Vale na próxima conclusão. */
export function useConfigurarSugestao() {
  const aoTerminar = useInvalidarOsTiposDeTarefa()
  return useMutation({
    mutationFn: ({
      tipoId,
      resultadoId,
      sugestao,
    }: {
      tipoId: string
      resultadoId: string
      sugestao: SugestaoPorResultado
    }) => configurarSugestao(tipoId, resultadoId, sugestao),
    onSuccess: aoTerminar,
  })
}

/* --- A Tarefa (4.2) ---------------------------------------------------------------- */

/**
 * Uma Tarefa: o título, o Tipo (com o rótulo **congelado**), o Responsável e o vínculo do FR-20.
 *
 * `tipo_nome` vem ao lado de `tipo_de_tarefa_id` de propósito, e a redundância é o FR-69: o `id` é
 * o que os relatórios contam, e o **nome** é o que a tela exibe. É por ele que a Tarefa antiga
 * continua mostrando o rótulo com que nasceu — sem que o SPA precise resolver o `id` num
 * `GET /tipos-de-tarefa` que já não devolve o Tipo excluído.
 *
 * `negocio_id` e `empresa_id` são nuláveis, e a nulidade é a informação: os dois nulos são a
 * Tarefa **solta**, que o FR-20 aceita. Os dois preenchidos não existem — o serviço recusa com
 * `400` e o banco com um `CHECK`.
 *
 * **`prazo` é o dado e `vencida` é a leitura dele** (4.3, FR-18). `prazo` chega como data **pura**
 * (`'2026-09-10'`), sem hora e sem fuso, e por isso ele nunca passa pelo formatador `DATA`:
 * `new Date('2026-09-10')` é meia-noite UTC, que já é o dia 9 em São Paulo. Quem o escreve na tela
 * é `dataPuraPorExtenso`, que não toca em `Date` nenhum.
 *
 * **`vencida` vem pronta do serviço, e o SPA não a recalcula.** É o mesmo precedente da cor da
 * régua, escrito no cabeçalho deste arquivo: derivá-la aqui a partir do prazo criaria uma segunda
 * régua, e as duas discordariam justamente na virada de dia — a lista filtrada por `atrasada`
 * mostraria uma linha que a tela pinta como no prazo. O serviço lê o relógio uma vez por resposta,
 * em `America/Sao_Paulo`, e a resposta inteira concorda consigo mesma.
 *
 * E sem campo de dinheiro, que é estrutural — a tabela não tem coluna de valor (FR-62).
 */
export type Tarefa = Esquemas['RespostaDeTarefa']

/**
 * O que a tela envia para criar uma Tarefa.
 *
 * Ele existe para ser o `satisfies` do corpo — é ele, e não a boa vontade de quem chama, que
 * garante que nada além dos seis campos chegue ao serviço (`extra="forbid"`), e que um
 * `{ tipo_nome: ... }` não compile.
 *
 * **`prazo` entrou na 4.3, e ele é obrigatório** (FR-18): omiti-lo deixou de compilar no mesmo
 * commit em que o serviço passou a exigi-lo, que é o que o contrato gerado compra (AD-16).
 */
export type NovaTarefa = Esquemas['CorpoDeNovaTarefa']

/** O corpo **completo** da edição (11.6, FR-80) — a mesma forma da criação. */
export type EdicaoDaTarefa = Esquemas['CorpoDaTarefa']

/** Um anexo da Tarefa (11.6, AD-9): nome original, MIME e tamanho. O arquivo, só pela rota. */
export type AnexoDaTarefa = Esquemas['RespostaDeAnexo']

/** A sugestão de próximo passo que a conclusão devolve (FR-21) — nunca uma Tarefa criada. */
export type SugestaoDeProximoPasso = Esquemas['RespostaDaSugestao']

/** A Tarefa concluída **e** a sugestão (11.6): a resposta de `PUT /tarefas/{id}/conclusao`. */
export type TarefaConcluida = Esquemas['RespostaDaConclusao']

/**
 * Prefixo de cache das Tarefas, **sob** `CHAVE_DOS_NEGOCIOS`.
 *
 * Ele fica sob o prefixo do quadro pela mesma razão que `chaveDaFicha` e `chaveDasMovimentacoes`:
 * quem invalida o prefixo do Negócio invalida as Tarefas junto, e é isso que faz uma escrita
 * disparada de qualquer tela deixar todas as leituras coerentes de uma vez. Excluir um Negócio, em
 * particular, apaga as Tarefas dele no banco pela cascata da FK — e sem estar sob o prefixo, o
 * cache continuaria mostrando as Tarefas de um Negócio que já não existe.
 *
 * Ele **não** é a chave do catálogo: `CHAVE_DOS_TIPOS_DE_TAREFA` responde o vocabulário (o seletor
 * de Tipo), e este responde o trabalho. Compartilhar as duas faria criar uma Tarefa reler o
 * catálogo inteiro, e renomear um Tipo na Administração reler as Tarefas de todos os Negócios
 * abertos.
 */
export const CHAVE_DAS_TAREFAS = [...CHAVE_DOS_NEGOCIOS, 'tarefas'] as const

/**
 * Um dos três recortes por prazo de `GET /tarefas` — o enum **gerado**, e não três literais.
 *
 * Ele vem do contrato (AD-16): acrescentar um quarto valor no serviço passa a compilar aqui
 * sozinho, e escrever `'ontem'` numa aba não compila. "Todas" **não** é um valor dele — é a
 * ausência de `quando`, e por isso os filtros da tela falam `RecorteDoPrazo | undefined`.
 */
export type RecorteDoPrazo = Esquemas['RecorteDoPrazo']

/**
 * Os filtros de `GET /tarefas`, os três opcionais — o parâmetro único das leituras de Tarefa.
 *
 * Ele é um objeto, e não três posicionais, porque os três são opcionais e independentes: com
 * posicionais, pedir só `concluida` obrigaria a escrever `undefined` duas vezes, e trocar a ordem
 * de dois deles compilaria em silêncio.
 */
export type FiltrosDeTarefas = {
  negocioId?: string
  quando?: RecorteDoPrazo
  concluida?: boolean
  /** Os filtros da tela de Tarefas (11.6) — todos opcionais, e se somam no serviço. */
  criadorId?: string
  /** Multi: "Eu" mais quem a pessoa escolher. Vazio é "sem filtro". */
  responsavelIds?: readonly string[]
  tipoDeTarefaId?: string
  empresaId?: string
  pessoaId?: string
}

/**
 * Chave de cache das Tarefas, com os **três** filtros dentro dela.
 *
 * O `negocioId` entra na chave porque a resposta é dele — o mesmo motivo pelo qual `chaveDaFicha`
 * leva o `id`: sem isso, abrir a ficha de um Negócio e navegar para a de outro reescreveria a
 * mesma entrada, e o segundo cartão mostraria as Tarefas do primeiro por um quadro de renderização.
 *
 * **`quando` e `concluida` entram pela mesma razão:** cada recorte é uma **resposta diferente** da
 * mesma rota. Fora da chave, trocar de aba escreveria a lista recortada por cima da anterior — e
 * voltar devolveria a errada do cache enquanto a releitura viaja, com a aba de um recorte e a lista
 * de outro. O discriminante é o que faz os quatro conviverem, e trocar de filtro voltar instantâneo
 * depois da primeira ida.
 *
 * **Os três são normalizados para `null`, e a normalização é a decisão** — não um descuido a
 * "consertar" depois. A chave é **posicional e de aridade fixa**, então a ausência não some dela:
 * ela vira um buraco na posição, e `undefined` num array é o que o `JSON.stringify` do React Query
 * já reescreve como `null`. Escrever `null` aqui é dizer isso no código em vez de depender da
 * serialização: a posição carrega um **valor** que significa "Todas" e "as duas metades", e não uma
 * ausência que cada leitor interpreta. Na URL ele continua sendo `undefined` — lá a ausência do
 * parâmetro é o que o serviço lê.
 */
export const chaveDasTarefas = ({
  negocioId,
  quando,
  concluida,
  criadorId,
  responsavelIds,
  tipoDeTarefaId,
  empresaId,
  pessoaId,
}: FiltrosDeTarefas = {}) =>
  [
    ...CHAVE_DAS_TAREFAS,
    negocioId ?? null,
    quando ?? null,
    concluida ?? null,
    criadorId ?? null,
    responsavelIds === undefined || responsavelIds.length === 0
      ? null
      : [...responsavelIds].sort().join(','),
    tipoDeTarefaId ?? null,
    empresaId ?? null,
    pessoaId ?? null,
  ] as const

/**
 * Lê as Tarefas visíveis (`200`) — **pendentes primeiro**, depois por prazo crescente.
 *
 * Só autenticada, como a ficha e a trilha: quem recorta é a RLS do serviço, linha a linha — a
 * Tarefa é entregue a quem é o **Responsável** dela e a quem já enxerga o Negócio dono. **Não há
 * recorte por Responsável**: a tela de Tarefas mostra o que a policy entrega, e o texto ao lado das
 * abas diz qual é o escopo. Quem pergunta "as minhas de hoje" é `GET /hoje`, com a fila do dia.
 *
 * **`200` com `[]`, e não `404`**, quando o Negócio não existe ou não é visível — e a diferença
 * para `listarMovimentacoes` é deliberada: a trilha lê o Negócio antes de listar, esta rota não.
 * O `negocio_id` aqui é **recorte**, e não autorização, e "não há, entre o que você enxerga,
 * Tarefa nenhuma daquele Negócio" é a resposta honesta.
 *
 * **`quando` são os três recortes por prazo da tela** (4.6): `atrasadas` já implica pendente
 * (é a regra do domínio, inteira), `hoje` e `futuras` falam só de prazo. Omiti-lo é a aba "Todas".
 * **`concluida` é a outra pergunta**, e a tela manda `false` nas quatro abas. Quem decide o que é
 * "hoje" é o serviço, em `America/Sao_Paulo` — o cliente não manda data nenhuma, e por isso não há
 * como a lista e o campo `vencida` discordarem.
 */
export function listarTarefas({
  negocioId,
  quando,
  concluida,
  criadorId,
  responsavelIds,
  tipoDeTarefaId,
  empresaId,
  pessoaId,
}: FiltrosDeTarefas = {}): Promise<Tarefa[]> {
  // `URLSearchParams` no lugar da concatenação à mão da 4.2, pelo molde de `listarEmpresas`: com
  // três parâmetros, o `?`/`&` vira decisão de quem escreve — e ela erra na primeira vez que um
  // deles ficar de fora.
  const consulta = new URLSearchParams()
  if (negocioId !== undefined) {
    consulta.set('negocio_id', negocioId)
  }
  if (quando !== undefined) {
    consulta.set('quando', quando)
  }
  // E ele só vai quando pedido. Um `concluida` sempre explícito diria a mesma coisa ao serviço
  // quando o valor fosse o padrão, mas mudaria a URL — e é a URL que a suíte compara para provar
  // que a ausência do filtro é a ausência do parâmetro.
  if (concluida !== undefined) {
    consulta.set('concluida', String(concluida))
  }
  // Os filtros da 11.6: o Responsável é multi — o parâmetro se repete.
  if (criadorId !== undefined) {
    consulta.set('criador_id', criadorId)
  }
  for (const responsavelId of responsavelIds ?? []) {
    consulta.append('responsavel_id', responsavelId)
  }
  if (tipoDeTarefaId !== undefined) {
    consulta.set('tipo_de_tarefa_id', tipoDeTarefaId)
  }
  if (empresaId !== undefined) {
    consulta.set('empresa_id', empresaId)
  }
  if (pessoaId !== undefined) {
    consulta.set('pessoa_id', pessoaId)
  }
  const query = consulta.toString()
  return chamar<Tarefa[]>('negocios', query === '' ? '/tarefas' : `/tarefas?${query}`)
}

/**
 * Cria a Tarefa (`201`) com o rótulo do Tipo já congelado nela.
 *
 * Exige `criar_tarefa`, que o FR-61-A concede aos quatro perfis. Criar **para outra pessoa** exige
 * também `atribuir_tarefa` — é o handoff da UJ-2, e quem não tem a claim recebe `403`; criar para
 * si continua passando só com a primeira.
 *
 * `400` quando o título vem em branco **ou** quando o corpo traz os dois vínculos do FR-20 juntos
 * (a Tarefa fica presa a um Negócio, ligada a uma Empresa, ou solta — nunca as duas coisas).
 * `404` quando o Tipo não existe ou foi excluído (FR-69), e `404` quando o Negócio não existe ou
 * não é visível — nunca `403` sobre o Negócio, que contaria a existência da linha.
 *
 * O corpo é montado por quem chama, e quem o fecha é o **parâmetro**: `NovaTarefa` é o apelido do
 * schema gerado, então o literal do chamador é recusado se trouxer campo a mais (`tipo_nome`)
 * ou se **faltar** um obrigatório (`prazo`, desde a 4.3) — um `satisfies` aqui dentro repetiria
 * o mesmo tipo que o parâmetro já declara e não afirmaria nada. `negocio_id` e `empresa_id` são
 * opcionais, e omiti-los é a Tarefa solta.
 */
export function criarTarefa(nova: NovaTarefa): Promise<Tarefa> {
  return chamar<Tarefa>('negocios', '/tarefas', {
    method: 'POST',
    corpo: nova,
  })
}

/**
 * As Tarefas visíveis, com os filtros que quem chama pedir.
 *
 * `retry: false` como as demais consultas do CRM: a recusa esperada é o `401` de sessão expirada,
 * que o cliente já renova sozinho antes de deixar o erro subir.
 *
 * `enabled` fica de fora de propósito: as duas superfícies que o chamam já são montadas com o que
 * precisam — o cartão dentro da ficha, que tem o `id`, e a tela de Tarefas, que não precisa de um.
 *
 * **Os filtros são parâmetro, e não estado deste hook**: quem os guarda é a tela, que desenha o
 * chip ou as abas. Trocar o valor troca a `queryKey`, e o React Query trata isso como o que é —
 * outra consulta —, servindo do cache a que já foi lida e buscando a que não foi. Um `refetch`
 * manual no clique da aba faria a mesma ida à rede sem nenhuma das duas coisas.
 */
export function useTarefas(filtros: FiltrosDeTarefas = {}, { habilitada = true } = {}) {
  return useQuery({
    queryKey: chaveDasTarefas(filtros),
    queryFn: () => listarTarefas(filtros),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Cria a Tarefa e relê a lista.
 *
 * Invalidar, e não remendar o cache com a resposta, é o ponto: a resposta é **uma** Tarefa, e o
 * que a tela desenha é a lista inteira, na ordem que o serviço decide.
 *
 * **Ela invalida o prefixo `CHAVE_DAS_TAREFAS`, e não só a entrada daquele Negócio.** Uma Tarefa
 * criada aqui pode ser solta (nenhum Negócio) ou de outro Negócio no dia em que a tela de Tarefas
 * (4.6) chamar esta mesma mutação — e invalidar só a entrada corrente deixaria as outras listas
 * mentindo. O custo é reler o que estiver em cache, que é uma ficha aberta.
 *
 * **E `CHAVE_DO_PAINEL_DE_HOJE` é a segunda linha desde a 4.6, porque ela não está sob a
 * primeira.** Painel do dia e Tarefas são chaves **irmãs** (`['negocios','hoje']` e
 * `['negocios','tarefas']`), e um prefixo não alcança o outro — é a mesma armadilha que
 * `useConcluirTarefa` já desarma com duas linhas. E ali o efeito é imediato e visível: a fila do
 * dia, `tarefas_para_hoje` e `sem_proximo_passo` mudam com a Tarefa nova. Sem esta linha, aceitar
 * a sugestão **na própria tela Hoje** deixaria os três congelados por até os 30 segundos de
 * `staleTime` — na mesma tela em que concluir move os números, que é a contradição que a pessoa
 * veria em dois cliques seguidos.
 *
 * **`CHAVE_DOS_NEGOCIOS` não entra, e a ausência é a diferença para `useConcluirTarefa`.** Criar
 * uma Tarefa não zera a régua de nada: `ultimo_contato_em` tem escritor único, e quem o chama é a
 * **conclusão** (FR-12). A cor do card e a das faixas continuam as mesmas — e como
 * `CHAVE_DAS_TAREFAS` mora sob `CHAVE_DOS_NEGOCIOS`, invalidar o prefixo largo releria a ficha e o
 * quadro inteiros para nada.
 *
 * **E ela não invalida `CHAVE_DOS_TIPOS_DE_TAREFA`:** criar uma Tarefa não move nada no
 * vocabulário. É a mesma separação que as sete mutações de Funil já fazem com a régua.
 */
export function useCriarTarefa() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (nova: NovaTarefa) => criarTarefa(nova),
    // A promessa continua sendo devolvida: `onSuccess` a aguarda, e é isso que mantém a mutação
    // "pendente" até a lista da tela já refletir o ato. `Promise.all` mantém a espera com duas
    // chaves, como em `useConcluirTarefa`.
    onSuccess: () =>
      Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_TAREFAS }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_PAINEL_DE_HOJE }),
      ]),
  })
}

/**
 * O que a tela envia para concluir uma Tarefa (4.4, FR-17).
 *
 * Ele existe para ser o `satisfies` do corpo — é ele, e não a boa vontade de quem chama, que
 * garante que nada além dos dois campos chegue ao serviço (`extra="forbid"`), e que um
 * `{ concluida_em: ... }` não compile.
 *
 * **O corpo inteiro é opcional, e `{}` é um pedido válido**: registrar o Resultado não é
 * obrigatório. E os dois campos são **alternativas** — o do catálogo e o texto livre do "Outros" —,
 * e mandá-los juntos é `400` no serviço.
 */
export type ConclusaoDaTarefa = Esquemas['CorpoDaConclusao']

/**
 * Conclui a Tarefa com o Resultado opcional (`200`), e zera a régua do Negócio (FR-12, FR-17).
 *
 * Exige `concluir_tarefa`, que o FR-61-A concede aos quatro perfis; quem a perdeu recebe `403`.
 *
 * `400` quando os dois Resultados vêm juntos **ou** quando o texto livre está em branco; `404`
 * quando a Tarefa não existe ou não é visível, quando o Resultado não é daquele Tipo ou foi
 * removido (FR-16), e quando o **Negócio** vinculado não é visível a quem está concluindo — nunca
 * `403` sobre nenhum dos dois, que contaria a existência da linha. `409` na **segunda** conclusão:
 * é transição de mão única, e nada é regravado nem republicado.
 *
 * A resposta é a mesma `Tarefa` que a lista devolve — mesmo montador no serviço —, já com
 * `concluida_em` e o Resultado congelado.
 */
export function concluirTarefa(
  tarefaId: string,
  conclusao: ConclusaoDaTarefa,
): Promise<TarefaConcluida> {
  return chamar<TarefaConcluida>('negocios', `/tarefas/${encodeURIComponent(tarefaId)}/conclusao`, {
    method: 'PUT',
    corpo: conclusao,
  })
}

/**
 * Conclui a Tarefa e relê **tudo o que é de Negócio**.
 *
 * **Ela invalida `CHAVE_DOS_NEGOCIOS`, e não `CHAVE_DAS_TAREFAS`** — e essa é a diferença entre
 * esta mutação e `useCriarTarefa`, que fica no prefixo estreito. Concluir move a **cor da régua**
 * do Negócio (FR-12): `ultimo_contato_em` avança na mesma transação, e o dado aparece na ficha
 * (`dias_sem_contato`, `cor`) e no card do quadro. Invalidar só a lista de tarefas deixaria a
 * linha marcada como concluída ao lado de um cabeçalho ainda vermelho, na mesma tela — e o
 * critério de aceite da história pede o contrário: a cor responde sem recarregar.
 *
 * O prefixo arrasta as tarefas junto, porque `CHAVE_DAS_TAREFAS` é montada **sob** ele. O custo é
 * reler o que estiver em cache, que é a ficha aberta e o que a pessoa visitou antes dela.
 *
 * **E `CHAVE_DO_PAINEL_DE_HOJE` é a segunda linha, porque ela não está sob a primeira.** Painel do
 * dia e quadro são chaves **irmãs** (`['negocios', 'hoje']` e `['negocios', 'quadro']`), e um
 * prefixo não alcança o outro — é a mesma armadilha que `useConfigurarRegua` já desarma com duas
 * linhas. E ali o efeito é maior que uma repintura: a régua decide no painel a **contagem** e a
 * **soma** de cada faixa, e zerar `ultimo_contato_em` move o Negócio de faixa. Sem esta linha, quem
 * concluísse a Tarefa na ficha e fosse para `/hoje` leria, por até os 30 segundos de `staleTime`,
 * um "No vermelho" que já não conta aquele Negócio — a tela do dia atrasada em relação ao ato que
 * acabou de acontecer.
 *
 * **`CHAVE_DOS_ORIGINADOS` não é**, e a ausência é decisão: `GET /originados` não devolve `cor`
 * nem `dias_sem_contato` (a linha é status, desfecho e datas), então zerar a régua não muda uma
 * coluna sequer daquela lista.
 *
 * **E ela não invalida `CHAVE_DOS_TIPOS_DE_TAREFA`:** concluir uma Tarefa não move nada no
 * vocabulário — nem quando o desfecho é um Resultado do catálogo.
 */
export function useConcluirTarefa() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: ({ tarefaId, conclusao }: { tarefaId: string; conclusao: ConclusaoDaTarefa }) =>
      concluirTarefa(tarefaId, conclusao),
    // A promessa continua sendo devolvida: `onSuccess` a aguarda, e é isso que mantém a mutação
    // "pendente" até a tela já refletir o ato. `Promise.all` mantém a espera com duas chaves.
    onSuccess: () =>
      Promise.all([
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_NEGOCIOS }),
        clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_PAINEL_DE_HOJE }),
      ]),
  })
}

/* --- Editar, excluir e anexar (11.6, FR-80, AD-9) --------------------------------------- */

function caminhoDaTarefa(tarefaId: string): string {
  return `/tarefas/${encodeURIComponent(tarefaId)}`
}

/**
 * Edita a Tarefa por inteiro (`PUT`, `200`) — **inclusive concluída** (FR-80).
 *
 * Quem pode: o Responsável, quem a criou e o escopo `todas` (`403` para os demais que a veem,
 * `404` para quem não a vê). O criador nunca muda.
 */
export function editarTarefa(tarefaId: string, edicao: EdicaoDaTarefa): Promise<Tarefa> {
  return chamar<Tarefa>('negocios', caminhoDaTarefa(tarefaId), { method: 'PUT', corpo: edicao })
}

/** Exclui a Tarefa (`204`) — inclusive concluída; os anexos vão junto (AD-9). */
export async function excluirTarefa(tarefaId: string): Promise<void> {
  await chamarComStatus<undefined>('negocios', caminhoDaTarefa(tarefaId), { method: 'DELETE' })
}

/**
 * Anexa um arquivo à Tarefa (`201`) — `multipart/form-data`, um arquivo por pedido.
 *
 * `422` para tipo de arquivo fora da lista ou acima do teto, com a frase do serviço.
 */
export function anexarArquivo(tarefaId: string, arquivo: File): Promise<AnexoDaTarefa> {
  const formulario = new FormData()
  formulario.append('arquivo', arquivo)
  return chamar<AnexoDaTarefa>('negocios', `${caminhoDaTarefa(tarefaId)}/anexos`, {
    method: 'POST',
    formulario,
  })
}

/**
 * O endereço do anexo — a **única** porta dele (nunca URL pública), com o cookie de quem olha.
 */
export function urlDoAnexo(tarefaId: string, anexoId: string): string {
  return montarUrl(
    'negocios',
    `${caminhoDaTarefa(tarefaId)}/anexos/${encodeURIComponent(anexoId)}`,
  )
}

/** As chaves que toda escrita de Tarefa relê: as Tarefas (sob os Negócios) e o painel. */
function useInvalidarAsTarefas() {
  const clienteDeConsultas = useQueryClient()
  return () =>
    Promise.all([
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_NEGOCIOS }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_PAINEL_DE_HOJE }),
    ])
}

/** Edita a Tarefa e relê as listas. */
export function useEditarTarefa() {
  const aoTerminar = useInvalidarAsTarefas()
  return useMutation({
    mutationFn: ({ tarefaId, edicao }: { tarefaId: string; edicao: EdicaoDaTarefa }) =>
      editarTarefa(tarefaId, edicao),
    onSuccess: aoTerminar,
  })
}

/** Exclui a Tarefa e relê as listas. */
export function useExcluirTarefa() {
  const aoTerminar = useInvalidarAsTarefas()
  return useMutation({
    mutationFn: (tarefaId: string) => excluirTarefa(tarefaId),
    onSuccess: aoTerminar,
  })
}

/** Anexa o arquivo e relê as listas (a Tarefa passa a listar o anexo). */
export function useAnexarArquivo() {
  const aoTerminar = useInvalidarAsTarefas()
  return useMutation({
    mutationFn: ({ tarefaId, arquivo }: { tarefaId: string; arquivo: File }) =>
      anexarArquivo(tarefaId, arquivo),
    onSuccess: aoTerminar,
  })
}

// --- Os Produtos do Negócio (5.6, FR-44, FR-45, AD-19) -------------------------------

/**
 * Uma linha de Produto do Negócio, como a tela a desenha.
 *
 * Os dois campos de dinheiro (`preco_unitario` e `valor_total`) são opcionais pelo mesmo mecanismo
 * do `valor` da ficha: para a SDR eles não chegam (FR-62). E são anuláveis além de omissíveis —
 * `null` quer dizer "este Produto não tem preço de venda cadastrado no ERP", que é um estado
 * legítimo e diferente de "não é da sua conta".
 *
 * **Não há campo de preço editável, e não há desconto.** O preço é do Carga Máquina (FR-45), e o
 * contrato não tem por onde recebê-lo.
 *
 * **A disponibilidade são dois campos, e chegam a todo perfil** (13.22a1): `disponibilidade` é o
 * estoque lido agora no Carga Máquina, no mesmo `GET` que relê os preços, e
 * `disponibilidade_consultada` diz se ele foi lido. Fora do Negócio em andamento, ou com o estoque
 * que não respondeu, vêm `null` e `false`. Nada disso é gravado no Negócio.
 */
export type ProdutoDoNegocio = Esquemas['RespostaDoProduto']

/**
 * A lista de Produtos com o **total**, que é o mesmo número que a ficha mostra.
 *
 * O total vem do serviço, e não é somado pela tela, por duas razões. A primeira é o FR-62: para a
 * SDR nenhum dos dois níveis sai, e um total montado aqui a partir das linhas que ela recebe seria
 * zero — a tela mentiria em vez de omitir. A segunda é o arredondamento: ele tem de ser o mesmo que
 * o fechamento grava no Negócio, e duas somas (uma em Python, outra em JavaScript) divergiriam no
 * centavo justamente no Negócio grande.
 */
export type ListaDeProdutos = Esquemas['RespostaDaListaDeProdutos']

/** O que a tela envia para acrescentar um Produto: **dois** campos, e nenhum deles é preço. */
export type NovoProduto = Esquemas['CorpoDoProduto']

/**
 * A marca que distingue a entrada de Produtos das outras sob o prefixo do quadro.
 *
 * Ela existe para o `predicate` de `useRelerOValorDoNegocio`, lá embaixo, e é uma constante — e não o literal repetido — porque
 * ela e `chaveDosProdutos` **têm de dizer a mesma coisa**: divergentes, o filtro deixaria de excluir
 * a consulta que ele existe para excluir, e o resultado seria um laço de leituras do ERP.
 */
const MARCA_DOS_PRODUTOS = 'produtos'

/**
 * Chave de cache dos Produtos de um Negócio.
 *
 * Ela fica **sob** `CHAVE_DOS_NEGOCIOS`, como `chaveDaFicha` e `chaveDasMovimentacoes`: quem
 * invalida o prefixo do quadro invalida os Produtos junto, e é isso que faz uma escrita disparada
 * de qualquer tela deixar todas as leituras de Negócio coerentes de uma vez.
 */
export const chaveDosProdutos = ({ negocioId }: { negocioId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, MARCA_DOS_PRODUTOS, negocioId] as const

function caminhoDosProdutos(negocioId: string): string {
  return `/negocios/${encodeURIComponent(negocioId)}/produtos`
}

/**
 * Lê os Produtos daquele Negócio e o total deles (`200`).
 *
 * **Este `GET` grava, e a escrita é deliberada** (AD-19): com o Negócio **em andamento** o serviço
 * relê os preços no Carga Máquina e regrava as linhas antes de responder — o preço do CRM é o do
 * ERP no instante do uso (FR-45). Com o Negócio ganho ou perdido ele devolve o que foi congelado, e
 * não toca o ERP: a lista funciona com o Carga Máquina fora do ar.
 *
 * `503` com a mensagem do FR-47 quando o ERP não responde, e aí **nenhuma** linha é regravada.
 * Exige `ver_produtos_do_negocio`; `404` quando o Negócio não existe ou a RLS não o entrega.
 */
export function listarProdutos(negocioId: string): Promise<ListaDeProdutos> {
  return chamar<ListaDeProdutos>('negocios', caminhoDosProdutos(negocioId))
}

/**
 * Acrescenta um Produto ao Negócio (`201`) — ou corrige a quantidade de um que já está lá (`200`).
 *
 * O corpo tem **dois** campos, e eles vão um a um: o do serviço é fechado (`extra="forbid"`), e a
 * tipagem estrutural do TypeScript não pega um campo a mais quando o objeto vem de uma variável.
 *
 * **Não há preço aqui, e não vai haver.** Ele é lido no ERP neste ato, do outro lado da fronteira.
 */
export function acrescentarProduto(
  negocioId: string,
  produto: NovoProduto,
): Promise<ProdutoDoNegocio> {
  return chamar<ProdutoDoNegocio>('negocios', caminhoDosProdutos(negocioId), {
    method: 'POST',
    corpo: {
      codigo: produto.codigo,
      quantidade: produto.quantidade,
    } satisfies NovoProduto,
  })
}

/** Remove um Produto do Negócio (`204`). Ela **não** fala com o ERP. */
export function removerProduto(negocioId: string, codigo: string): Promise<void> {
  return chamar<void>(
    'negocios',
    `${caminhoDosProdutos(negocioId)}/${encodeURIComponent(codigo)}`,
    {
      method: 'DELETE',
    },
  )
}

/**
 * Os Produtos de um Negócio.
 *
 * `retry: false` pela mesma razão da ficha, e aqui ela pesa mais: a recusa esperada é o `503` do
 * FR-47, e insistir seguraria a tela por três prazos do ERP antes de dizer o que já se sabia.
 *
 * **`habilitada` pelo mesmo caso da ficha**: excluído o Negócio, os Produtos morrem junto (a
 * cascata da FK), e um refetch receberia o mesmo `404`.
 */
export function useProdutos(negocioId: string, { habilitada = true } = {}) {
  return useQuery({
    queryKey: chaveDosProdutos({ negocioId }),
    queryFn: () => listarProdutos(negocioId),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Invalida os Produtos **e a ficha** daquele Negócio.
 *
 * As duas, e não só a primeira: o `valor` que a ficha mostra é a **soma** das linhas enquanto o
 * Negócio está em andamento (AD-19), e uma escrita de Produto que não invalidasse a ficha deixaria
 * o cabeçalho mostrando o total de antes — o número errado ao lado da lista certa, na mesma tela.
 *
 * O quadro, o catálogo, o painel do dia e os Originados entram junto por `useInvalidarOQuadroEO
 * Catalogo`, pelo mesmo motivo: o card do Funil e a faixa do dia também carregam o valor do Negócio.
 */
function useInvalidarOsProdutos(negocioId: string) {
  const clienteDeConsultas = useQueryClient()
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return async () => {
    await clienteDeConsultas.invalidateQueries({ queryKey: chaveDosProdutos({ negocioId }) })
    /* 13.22b2: mudar o painel muda qual foto é a vigente (a marca da lista). */
    await clienteDeConsultas.invalidateQueries({ queryKey: chaveDasFotos({ negocioId }) })
    await aoTerminar()
  }
}

/**
 * Acrescenta o Produto e relê tudo o que o ato mudou.
 *
 * O `negocioId` vem do **hook**, e não da chamada: quem acrescenta é o cartão daquele Negócio, e
 * passá-lo a cada `mutate` abriria a chance de a tela gravar num Negócio e invalidar outro.
 */
export function useAcrescentarProduto(negocioId: string) {
  const aoTerminar = useInvalidarOsProdutos(negocioId)
  return useMutation({
    mutationFn: (produto: NovoProduto) => acrescentarProduto(negocioId, produto),
    onSuccess: aoTerminar,
  })
}

/** Remove o Produto e relê tudo o que o ato mudou — inclusive o valor da ficha. */
export function useRemoverProduto(negocioId: string) {
  const aoTerminar = useInvalidarOsProdutos(negocioId)
  return useMutation({
    mutationFn: (codigo: string) => removerProduto(negocioId, codigo),
    onSuccess: aoTerminar,
  })
}

/**
 * Relê tudo o que mostra o **valor** de um Negócio — menos os Produtos dele (5.6, AD-19).
 *
 * Ela existe por uma corrida que só aparece na primeira abertura depois de o preço mudar no ERP: a
 * ficha e os Produtos são lidos **em paralelo**, e é a leitura dos Produtos que faz o serviço reler
 * os preços e regravar as linhas. A ficha, portanto, foi respondida com o total **de antes** — e a
 * tela mostra o cartão com o número novo e o cabeçalho com o velho, ao mesmo tempo.
 *
 * **A exclusão dos Produtos não é um detalhe: sem ela isto é um laço.** `chaveDosProdutos` mora sob
 * `CHAVE_DOS_NEGOCIOS`, então invalidar o prefixo inteiro refaria a leitura de Produtos — que vai ao
 * Carga Máquina, regrava as linhas e dispara esta mesma reconciliação de novo. O `predicate` é o que
 * mantém a releitura em **uma** volta, e é por isso que ela não reusa
 * `useInvalidarOQuadroEOCatalogo` (que invalida o prefixo cru, e é o certo para as *mutações*: lá
 * refazer a leitura de Produtos é exatamente o que se quer).
 */
export function useRelerOValorDoNegocio() {
  const clienteDeConsultas = useQueryClient()
  return async () => {
    await Promise.all([
      clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DOS_NEGOCIOS,
        predicate: (consulta) => consulta.queryKey[2] !== MARCA_DOS_PRODUTOS,
      }),
      clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DO_PAINEL_DE_HOJE }),
    ])
  }
}

// --- As RATs do Negócio (6.1 + 6.3, FR-48, FR-51, FR-52, FR-62) ----------------------

/**
 * Uma RAT como a tela a desenha — sete campos, e **nenhum** deles é dinheiro.
 *
 * A ausência é estrutural, e não um recorte por perfil como o dos Produtos: a tabela `rat` não tem
 * `valor`, `custo` nem `margem` em coluna nenhuma, e o response model do serviço tem schema
 * fechado. Não há `preco_unitario` opcional a tratar aqui porque não há o que esconder.
 *
 * **`numero_do_pedido` é o campo por onde o cartão agrupa**, e ele é o snapshot que a abertura
 * copiou do Negócio — não o `numero_do_pedido` que a ficha mostra hoje. É essa diferença que faz
 * "um Negócio com dois Pedidos mostra as RATs de cada um separadamente" (FR-51) valer sem depender
 * de um campo que pode mudar.
 */
export type Rat = Esquemas['RespostaDaRat']

/** O envelope da listagem: um objeto com `itens`, e não um array na raiz. */
export type ListaDeRats = Esquemas['RespostaDaListaDeRats']

/** O que a tela envia para abrir uma RAT: **dois** campos, e nenhum deles é o Pedido. */
export type NovaRat = Esquemas['CorpoDaRat']

/** Onde a RAT está no atendimento — os três valores do FR-49, fechados pelo contrato. */
export type StatusDaRat = Rat['status']

/**
 * O corpo do `PUT` de status da RAT: **um** campo, e ele é um dos três valores.
 *
 * O nome segue o padrão dos vizinhos (`NovaRat`, `ListaDeRats`) e diz de que **status** se fala.
 * Um `StatusPedido` leria como "status do pedido" num módulo onde `numero_do_pedido` é o Pedido do
 * ERP — e o Pedido não tem status nenhum neste contrato.
 */
export type NovoStatusDaRat = Esquemas['CorpoDoStatusDaRat']

/**
 * Chave de cache das RATs de um Negócio.
 *
 * Ela fica **sob** `CHAVE_DOS_NEGOCIOS`, como `chaveDaFicha`, `chaveDasMovimentacoes` e
 * `chaveDosProdutos`: quem invalida o prefixo do quadro invalida as RATs junto, e é isso que faz
 * uma escrita disparada de qualquer tela deixar todas as leituras de Negócio coerentes de uma vez.
 */
export const chaveDasRats = ({ negocioId }: { negocioId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, 'rats', negocioId] as const

function caminhoDasRats(negocioId: string): string {
  return `/negocios/${encodeURIComponent(negocioId)}/rats`
}

/**
 * Lê as RATs daquele Negócio (`200`), da mais nova para a mais velha.
 *
 * **Sem permissão de rota**: "ver a RAT" não é uma linha do catálogo do FR-61-A — quem recorta é a
 * RLS, exatamente como na ficha. `404` quando o Negócio não existe ou a RLS não o entrega; Negócio
 * visível sem reclamação responde `200` com `itens: []`.
 *
 * A lista chega **plana**: quem agrupa por `numero_do_pedido` é o cartão.
 */
export function listarRats(negocioId: string): Promise<ListaDeRats> {
  return chamar<ListaDeRats>('negocios', caminhoDasRats(negocioId))
}

/**
 * Abre uma RAT no Negócio (`201`).
 *
 * O corpo tem **dois** campos, e eles vão um a um: o do serviço é fechado (`extra="forbid"`), e a
 * tipagem estrutural do TypeScript não pega um campo a mais quando o objeto vem de uma variável.
 *
 * **Não há `numero_do_pedido` aqui, e não vai haver**: ele é copiado do Negócio dentro da transação
 * (FR-48), e aceitá-lo seria a porta para pendurar a reclamação no Pedido errado. `400`
 * `negocio_sem_pedido` quando o Negócio ainda não tem Pedido liberado.
 */
export function abrirRat(negocioId: string, rat: NovaRat): Promise<Rat> {
  return chamar<Rat>('negocios', caminhoDasRats(negocioId), {
    method: 'POST',
    corpo: {
      descricao_do_problema: rat.descricao_do_problema,
      numero_da_op: rat.numero_da_op,
    } satisfies NovaRat,
  })
}

/**
 * Move a RAT para outro status (`200`).
 *
 * O caminho carrega os **dois** ids, e o par é conferido do outro lado: com o `rat_id` sozinho,
 * `PUT /negocios/A/rats/{rat de B}/status` funcionaria quando as duas Carteiras fossem da mesma
 * pessoa. Status igual ao atual é `200` sem efeito nenhum — o serviço trata o repetido como no-op.
 */
export function alterarStatusDaRat(
  negocioId: string,
  ratId: string,
  status: StatusDaRat,
): Promise<Rat> {
  return chamar<Rat>(
    'negocios',
    `${caminhoDasRats(negocioId)}/${encodeURIComponent(ratId)}/status`,
    { method: 'PUT', corpo: { status } satisfies NovoStatusDaRat },
  )
}

/**
 * As RATs de um Negócio.
 *
 * `retry: false` pela mesma razão da ficha: a recusa esperada é o `404` do Negócio que a RLS não
 * entrega, e insistir seguraria a tela para repetir uma resposta que não vai mudar.
 *
 * **`habilitada` pelo mesmo caso da ficha**: excluído o Negócio, as RATs morrem junto (a cascata da
 * FK), e um refetch receberia o mesmo `404`.
 */
export function useRats(negocioId: string, { habilitada = true } = {}) {
  return useQuery({
    queryKey: chaveDasRats({ negocioId }),
    queryFn: () => listarRats(negocioId),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Invalida **só** a lista de RATs daquele Negócio.
 *
 * Ela é mais estreita que `useInvalidarOsProdutos`, e a diferença é o que cada escrita muda: linha
 * de Produto muda o `valor` do Negócio — e portanto a ficha, o card do Funil e a faixa do dia —,
 * enquanto abrir uma RAT ou mover o status dela não muda campo nenhum de Negócio. Invalidar o
 * quadro aqui recarregaria quatro telas para redesenhar um cartão.
 */
function useInvalidarAsRats(negocioId: string) {
  const clienteDeConsultas = useQueryClient()
  return () => clienteDeConsultas.invalidateQueries({ queryKey: chaveDasRats({ negocioId }) })
}

/**
 * Abre a RAT e relê a lista.
 *
 * O `negocioId` vem do **hook**, e não da chamada: quem abre é o cartão daquele Negócio, e passá-lo
 * a cada `mutate` abriria a chance de a tela gravar num Negócio e invalidar outro.
 */
export function useAbrirRat(negocioId: string) {
  const aoTerminar = useInvalidarAsRats(negocioId)
  return useMutation({
    mutationFn: (rat: NovaRat) => abrirRat(negocioId, rat),
    onSuccess: aoTerminar,
  })
}

/**
 * Move o status e relê a lista.
 *
 * A releitura não é decorativa: `atualizado_em` mudou do outro lado, e a lista é a única leitura
 * que o cartão tem. Remendar o cache com a resposta economizaria uma volta e deixaria a lista
 * divergir da tabela no dia em que a mudança de status passar a mexer em outra coisa.
 */
export function useAlterarStatusDaRat(negocioId: string) {
  const aoTerminar = useInvalidarAsRats(negocioId)
  return useMutation({
    mutationFn: ({ ratId, status }: { ratId: string; status: StatusDaRat }) =>
      alterarStatusDaRat(negocioId, ratId, status),
    onSuccess: aoTerminar,
  })
}

/* --- A Previsão de fechamento (FR-78, 11.3) -------------------------------------------------- */

/** Um item da Previsão de fechamento: rótulo, cor da paleta pastel fechada e descrição. */
export type PrevisaoDeFechamento = Esquemas['RespostaDaPrevisao']

/** A cor de um item — os oito nomes do `DESIGN.md`, e nenhum outro (sem seletor livre). */
export type CorPastel = Esquemas['CorPastel']

/** O corpo de cadastrar um item. */
export type NovaPrevisao = Esquemas['CorpoDaPrevisao']

/** O corpo de editar um item — completo, com a posição na lista. */
export type EdicaoDaPrevisao = Esquemas['CorpoDeEdicaoDaPrevisao']

/**
 * As oito cores da paleta, na ordem do `DESIGN.md`, com o nome que a tela escreve ao lado do ponto
 * (cor sozinha não carrega significado). `Record<CorPastel, …>` é o guarda de exaustividade.
 */
export const NOMES_DAS_CORES: Record<CorPastel, string> = {
  areia: 'Areia',
  salvia: 'Sálvia',
  ceu: 'Céu',
  lavanda: 'Lavanda',
  rose: 'Rosé',
  pessego: 'Pêssego',
  nevoa: 'Névoa',
  oliva: 'Oliva',
}

/** As cores na ordem da paleta — derivadas do mapa, e não redigitadas. */
export const CORES_PASTEL = Object.keys(NOMES_DAS_CORES) as readonly CorPastel[]

export const CHAVE_DA_PREVISAO = ['negocios', 'previsoes-de-fechamento'] as const

function caminhoDaPrevisao(previsaoId: string): string {
  return `/previsoes-de-fechamento/${encodeURIComponent(previsaoId)}`
}

/** Lê os itens **ativos** (`200`), na ordem da lista — os três de fábrica numa base nova. */
export function lerPrevisoes(): Promise<PrevisaoDeFechamento[]> {
  return chamar<PrevisaoDeFechamento[]>('negocios', '/previsoes-de-fechamento')
}

/** O desfecho do cadastro: o item, e se ele foi **reativado** (`200`, mesmo `id`). */
export interface PrevisaoCadastrada {
  previsao: PrevisaoDeFechamento
  restaurado: boolean
}

/**
 * Cadastra o item (`201`) — ou reativa o excluído com o mesmo rótulo (`200`). `409` quando o
 * rótulo já existe ativo; `422` cor fora da paleta ou rótulo vazio; `403` sem a permissão.
 */
export async function cadastrarPrevisao(nova: NovaPrevisao): Promise<PrevisaoCadastrada> {
  const { corpo, status } = await chamarComStatus<PrevisaoDeFechamento>(
    'negocios',
    '/previsoes-de-fechamento',
    {
      method: 'POST',
      corpo: {
        rotulo: nova.rotulo,
        cor: nova.cor,
        descricao: nova.descricao,
      } satisfies NovaPrevisao,
    },
  )
  return { previsao: corpo, restaurado: status === 200 }
}

/** Grava o item como ele deve ficar (`200`). */
export function editarPrevisao(
  previsaoId: string,
  edicao: EdicaoDaPrevisao,
): Promise<PrevisaoDeFechamento> {
  return chamar<PrevisaoDeFechamento>('negocios', caminhoDaPrevisao(previsaoId), {
    method: 'PUT',
    corpo: {
      rotulo: edicao.rotulo,
      cor: edicao.cor,
      descricao: edicao.descricao,
      ordem: edicao.ordem,
    } satisfies EdicaoDaPrevisao,
  })
}

/** Exclui o item (`200` com `ativo: false`) — exclusão **lógica** (FR-78). */
export function excluirPrevisao(previsaoId: string): Promise<PrevisaoDeFechamento> {
  return chamar<PrevisaoDeFechamento>('negocios', caminhoDaPrevisao(previsaoId), {
    method: 'DELETE',
  })
}

/** A chave de um item lido por `id` — sob o prefixo do catálogo, que a administração invalida. */
function chaveDoItemDePrevisao(previsaoId: string) {
  return [...CHAVE_DA_PREVISAO, 'item', previsaoId] as const
}

/**
 * Lê um item por `id`, **ativo ou não** (`200`) — o rótulo e a cor do item que um Negócio já
 * escolheu e que a gestão depois excluiu (FR-78: excluir não desmarca). `404` quando não existe.
 */
export function lerPrevisao(previsaoId: string): Promise<PrevisaoDeFechamento> {
  return chamar<PrevisaoDeFechamento>('negocios', caminhoDaPrevisao(previsaoId))
}

/**
 * O catálogo ativo da Previsão.
 *
 * `habilitada` existe para o quadro (11.8): sem nenhum card com previsão marcada, não há o que
 * rotular, e o catálogo nem é pedido.
 */
export function usePrevisoes({ habilitada = true } = {}) {
  return useQuery({
    queryKey: CHAVE_DA_PREVISAO,
    queryFn: lerPrevisoes,
    retry: false,
    enabled: habilitada,
  })
}

/** O desfecho de um lote de leituras por `id` (os itens excluídos que algum Negócio aponta). */
interface LoteDeItens {
  itens: PrevisaoDeFechamento[]
  /** Algum ainda está vindo. */
  pendente: boolean
  /** Refaz as leituras que falharam. */
  refazer: () => void
}

/**
 * Função de módulo, e não inline: o `combine` estável é o que deixa o TanStack Query devolver o
 * mesmo objeto enquanto nada mudou. O id que falhou sai do `queryKey` (`[..., 'item', id]`).
 */
function combinarOLote(resultados: UseQueryResult<PrevisaoDeFechamento>[]): LoteDeItens {
  const falhos = resultados.filter((resultado) => resultado.isError)
  return {
    itens: resultados
      .map((resultado) => resultado.data)
      .filter((item): item is PrevisaoDeFechamento => item !== undefined),
    pendente: resultados.some((resultado) => resultado.isPending),
    refazer: () => {
      for (const resultado of falhos) {
        void resultado.refetch()
      }
    },
  }
}

/** O que a tela sabe dos itens de Previsão que precisa desenhar (11.8). */
export interface ItensDePrevisao {
  /** Os ativos, na ordem da lista — as escolhas novas (o seletor dos Dados do negócio). */
  ativos: readonly PrevisaoDeFechamento[]
  /** Todo item conhecido por `id`: os ativos **e** os excluídos que algum Negócio ainda aponta. */
  porId: ReadonlyMap<string, PrevisaoDeFechamento>
  /** O catálogo ativo — ou algum item lido por `id` — ainda está vindo. */
  carregando: boolean
  /** O catálogo ativo não veio. */
  catalogoFalhou: boolean
  /** Os ids em uso que **não** puderam ser rotulados: a leitura por `id` falhou (ou deu `404`). */
  falharam: ReadonlySet<string>
  /** O catálogo, ou alguma leitura por `id`, falhou. */
  falhou: boolean
  /** Refaz de verdade o que falhou: o catálogo e as leituras por `id`. */
  tentarDeNovo: () => void
}

/**
 * Os itens de Previsão que a tela precisa rotular — o catálogo ativo (`GET` da 11.3) mais, por
 * `id`, **só** os que não estão nele (os excluídos que um Negócio ainda aponta). É a composição do
 * AD-25: rótulo, cor e descrição vêm do `negocios`, e não viajam no card nem no evento.
 *
 * Uma consulta pelo catálogo e uma por item excluído — nunca uma por card: os ids repetidos viram
 * um só, e o cache por `id` é o mesmo em toda tela. **Falha não vira "sem previsão"**: o id que não
 * pôde ser rotulado sai em `falharam`, para a tela dizer isso em vez de sumir com a marca.
 */
export function useItensDePrevisao(
  idsEmUso: readonly (string | null | undefined)[],
  { habilitada = true } = {},
): ItensDePrevisao {
  const catalogo = usePrevisoes({ habilitada })
  const ativos = useMemo(() => catalogo.data ?? [], [catalogo.data])
  const faltando = useMemo(() => {
    if (!catalogo.isSuccess) {
      return []
    }
    const conhecidos = new Set(ativos.map((item) => item.id))
    const unicos = new Set<string>()
    for (const id of idsEmUso) {
      if (id != null && !conhecidos.has(id)) {
        unicos.add(id)
      }
    }
    return [...unicos].sort()
  }, [idsEmUso, ativos, catalogo.isSuccess])
  const lote = useQueries({
    queries: faltando.map((id) => ({
      queryKey: chaveDoItemDePrevisao(id),
      queryFn: () => lerPrevisao(id),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
    combine: combinarOLote,
  })
  const porId = useMemo(
    () => new Map([...ativos, ...lote.itens].map((item) => [item.id, item] as const)),
    [ativos, lote.itens],
  )
  /* Os ids que ficaram sem rótulo depois de toda leitura terminar: é falha, e não "sem previsão". */
  const falharam = useMemo(
    () =>
      lote.pendente
        ? new Set<string>()
        : new Set(faltando.filter((id) => !porId.has(id))),
    [faltando, porId, lote.pendente],
  )
  const { refetch: relerOCatalogo } = catalogo
  const { refazer } = lote
  const tentarDeNovo = useCallback(() => {
    void relerOCatalogo()
    refazer()
  }, [relerOCatalogo, refazer])
  const carregando = habilitada && (catalogo.isPending || lote.pendente)
  const catalogoFalhou = catalogo.isError
  /* Um objeto estável enquanto nada muda: quem o usa em `useMemo`/`useCallback` não recalcula a
     cada render. */
  return useMemo(
    () => ({
      ativos,
      porId,
      carregando,
      catalogoFalhou,
      falharam,
      falhou: catalogoFalhou || falharam.size > 0,
      tentarDeNovo,
    }),
    [ativos, porId, carregando, catalogoFalhou, falharam, tentarDeNovo],
  )
}

function useInvalidarAPrevisao() {
  const clienteDeConsultas = useQueryClient()
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DA_PREVISAO })
}

/** Cadastra (ou reativa) o item e relê o catálogo. */
export function useCadastrarPrevisao() {
  const aoTerminar = useInvalidarAPrevisao()
  return useMutation({
    mutationFn: (nova: NovaPrevisao) => cadastrarPrevisao(nova),
    onSuccess: aoTerminar,
  })
}

/** Edita o item e relê o catálogo. */
export function useEditarPrevisao() {
  const aoTerminar = useInvalidarAPrevisao()
  return useMutation({
    mutationFn: ({ previsaoId, edicao }: { previsaoId: string; edicao: EdicaoDaPrevisao }) =>
      editarPrevisao(previsaoId, edicao),
    onSuccess: aoTerminar,
  })
}

/** Exclui logicamente o item e relê o catálogo. */
export function useExcluirPrevisao() {
  const aoTerminar = useInvalidarAPrevisao()
  return useMutation({
    mutationFn: (previsaoId: string) => excluirPrevisao(previsaoId),
    onSuccess: aoTerminar,
  })
}

/* --- A oportunidade automática: a regra de cada usuário (13.21a, FR-66 emendado) ------------ */

/**
 * A regra de um usuário: o Funil (o da Etapa) e a Etapa em que nasce o Negócio sem Empresa de todo
 * número novo que conversar com ele. Sem regra, nada nasce (13.21a1).
 */
export type RegraDeOportunidade = Esquemas['RespostaDaRegraDeOportunidade']

/** O corpo de gravar a regra: o Funil vai junto para o serviço conferir que a Etapa é dele. */
export type CorpoDaRegraDeOportunidade = Esquemas['CorpoDaRegraDeOportunidade']

/**
 * Chave própria: a regra é outro recurso, e não o catálogo de Funis — gravá-la não muda contagem
 * nenhuma, e reler os Funis a cada escolha faria a tela de Funis piscar à toa.
 */
export const CHAVE_DAS_REGRAS_DE_OPORTUNIDADE = ['negocios', 'regras-de-oportunidade'] as const

function caminhoDaRegra(usuarioId: string): string {
  return `/regras-de-oportunidade/${encodeURIComponent(usuarioId)}`
}

/** Lê as regras (`200`). Guarda `gerir_funis_e_etapas`; os demais recebem `403`. */
export function lerRegrasDeOportunidade(): Promise<RegraDeOportunidade[]> {
  return chamar<RegraDeOportunidade[]>('negocios', '/regras-de-oportunidade')
}

/**
 * Grava ou troca a regra do usuário (`200`). `404` Funil ou Etapa inexistente; `422` a Etapa de
 * outro Funil, ou o usuário fora da réplica ou inativo.
 */
export function gravarRegraDeOportunidade(
  usuarioId: string,
  regra: CorpoDaRegraDeOportunidade,
): Promise<RegraDeOportunidade> {
  return chamar<RegraDeOportunidade>('negocios', caminhoDaRegra(usuarioId), {
    method: 'PUT',
    corpo: { funil_id: regra.funil_id, etapa_id: regra.etapa_id } satisfies CorpoDaRegraDeOportunidade,
  })
}

/** Apaga a regra do usuário (`204`, idempotente): os números novos dele deixam de virar Negócio. */
export function apagarRegraDeOportunidade(usuarioId: string): Promise<void> {
  return chamar<void>('negocios', caminhoDaRegra(usuarioId), { method: 'DELETE' })
}

/**
 * As regras de todos.
 *
 * `habilitada` fecha a porta antes da rede, como a de `useUsuarios`: quem monta a seção decide pela
 * permissão da sessão, e um hook roda antes de qualquer `return null`.
 */
export function useRegrasDeOportunidade({ habilitada = true }: { habilitada?: boolean } = {}) {
  return useQuery({
    queryKey: CHAVE_DAS_REGRAS_DE_OPORTUNIDADE,
    queryFn: lerRegrasDeOportunidade,
    enabled: habilitada,
    retry: false,
  })
}

/**
 * O desfecho comum das duas escritas: reler as regras. A promessa volta ao `onSuccess`, e a mutação
 * só deixa de estar pendente com a releitura feita — a linha destrava já mostrando o gravado.
 */
function useInvalidarAsRegras() {
  const clienteDeConsultas = useQueryClient()
  return () =>
    clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_REGRAS_DE_OPORTUNIDADE })
}

/** Grava a regra de um usuário e relê as regras. */
export function useGravarRegraDeOportunidade() {
  const aoTerminar = useInvalidarAsRegras()
  return useMutation({
    mutationFn: ({ usuarioId, regra }: { usuarioId: string; regra: CorpoDaRegraDeOportunidade }) =>
      gravarRegraDeOportunidade(usuarioId, regra),
    onSuccess: aoTerminar,
  })
}

/** Apaga a regra de um usuário e relê as regras. */
export function useApagarRegraDeOportunidade() {
  const aoTerminar = useInvalidarAsRegras()
  return useMutation({
    mutationFn: (usuarioId: string) => apagarRegraDeOportunidade(usuarioId),
    onSuccess: aoTerminar,
  })
}

/* --- Os campos personalizados de Negócio (FR-79, 11.3) --------------------------------------- */

/** A administração dos campos personalizados de Negócio — a forma é a mesma do `cadastro`. */
export const camposDoNegocio = criarApiDeCamposPersonalizados('negocios')

// --- A Nota do Negócio (11.4) ---------------------------------------------------------------

/**
 * Uma nota do Negócio: texto livre do histórico de atividades do modal. `editavel` diz se quem
 * está olhando pode editar e excluir (autor ou escopo `todas`) — conveniência da tela; quem
 * decide é o serviço. Nota **não** é contato: não zera a régua (AD-10).
 */
export type Nota = Esquemas['RespostaDaNota']

/** O corpo de criar e de editar uma nota: o texto, e só. */
export type CorpoDaNota = Esquemas['CorpoDaNota']

/**
 * O teto do texto da nota — o mesmo `max_length` do serviço (`TAMANHO_DO_TEXTO_DA_NOTA`, 5000, em
 * `servicos/negocios/dominio/nota.py`). Ele é escrito aqui porque o tipo gerado (`CorpoDaNota`)
 * não carrega `maxLength` — o `openapi-typescript` não publica restrições de valor. O
 * `api-da-11-4.test.ts` prende o valor ao literal do serviço; quem mudar um lado muda o outro.
 */
export const TAMANHO_DO_TEXTO_DA_NOTA = 5000

/**
 * Chave de cache das notas de um Negócio — **sob** `CHAVE_DOS_NEGOCIOS`, como a ficha e a trilha:
 * a invalidação do prefixo, que toda escrita de Negócio já faz, alcança as notas também.
 */
export const chaveDasNotas = ({ negocioId }: { negocioId: string }) =>
  [...CHAVE_DOS_NEGOCIOS, 'notas', negocioId] as const

function caminhoDasNotas(negocioId: string): string {
  return `${caminhoDoNegocio(negocioId)}/notas`
}

function caminhoDaNota(negocioId: string, notaId: string): string {
  return `${caminhoDasNotas(negocioId)}/${encodeURIComponent(notaId)}`
}

/** As notas do Negócio (`200`), fixadas primeiro e depois da mais nova. `403` fora do alcance. */
export function listarNotas(negocioId: string): Promise<Nota[]> {
  return chamar<Nota[]>('negocios', caminhoDasNotas(negocioId))
}

/** Cria a nota (`201`) em nome de quem está logado. `422` para texto vazio. */
export function criarNota(negocioId: string, texto: string): Promise<Nota> {
  return chamar<Nota>('negocios', caminhoDasNotas(negocioId), {
    method: 'POST',
    corpo: { texto } satisfies CorpoDaNota,
  })
}

/** Troca o texto (`200`). `403` para quem não é o autor nem tem escopo `todas`. */
export function editarNota(negocioId: string, notaId: string, texto: string): Promise<Nota> {
  return chamar<Nota>('negocios', caminhoDaNota(negocioId, notaId), {
    method: 'PUT',
    corpo: { texto } satisfies CorpoDaNota,
  })
}

/** Exclui a nota (`204`) — exclusão física. Mesmas recusas da edição. */
export function excluirNota(negocioId: string, notaId: string): Promise<void> {
  return chamar<void>('negocios', caminhoDaNota(negocioId, notaId), { method: 'DELETE' })
}

/** Fixa ou desafixa (`200`). Quem vê o Negócio, fixa — não é só do autor. */
export function fixarNota(negocioId: string, notaId: string, fixada: boolean): Promise<Nota> {
  return chamar<Nota>('negocios', `${caminhoDaNota(negocioId, notaId)}/fixacao`, {
    method: 'PUT',
    corpo: { fixada } satisfies Esquemas['CorpoDaFixacao'],
  })
}

/** As notas do Negócio. `habilitada: false` desliga a leitura (Negócio acabou de ser excluído). */
export function useNotas(negocioId: string, { habilitada = true } = {}) {
  return useQuery({
    queryKey: chaveDasNotas({ negocioId }),
    queryFn: () => listarNotas(negocioId),
    enabled: habilitada,
    retry: false,
  })
}

/** O desfecho das escritas de nota: reler **só** as notas daquele Negócio (a régua não muda). */
function useInvalidarAsNotas(negocioId: string) {
  const clienteDeConsultas = useQueryClient()
  return () => clienteDeConsultas.invalidateQueries({ queryKey: chaveDasNotas({ negocioId }) })
}

export function useCriarNota(negocioId: string) {
  const aoTerminar = useInvalidarAsNotas(negocioId)
  return useMutation({
    mutationFn: (texto: string) => criarNota(negocioId, texto),
    onSuccess: aoTerminar,
  })
}

export function useEditarNota(negocioId: string) {
  const aoTerminar = useInvalidarAsNotas(negocioId)
  return useMutation({
    mutationFn: ({ notaId, texto }: { notaId: string; texto: string }) =>
      editarNota(negocioId, notaId, texto),
    onSuccess: aoTerminar,
  })
}

export function useExcluirNota(negocioId: string) {
  const aoTerminar = useInvalidarAsNotas(negocioId)
  return useMutation({
    mutationFn: (notaId: string) => excluirNota(negocioId, notaId),
    onSuccess: aoTerminar,
  })
}

export function useFixarNota(negocioId: string) {
  const aoTerminar = useInvalidarAsNotas(negocioId)
  return useMutation({
    mutationFn: ({ notaId, fixada }: { notaId: string; fixada: boolean }) =>
      fixarNota(negocioId, notaId, fixada),
    onSuccess: aoTerminar,
  })
}

/* --- Os Negócios de um lote de Empresas (13.9, a ficha do Grupo) --------------------------- */

/**
 * Um Negócio como a ficha do Grupo o soma: **qualquer** status, com o número do Pedido e
 * `com_orcamento`. `valor` é texto de duas casas, `null` ("sem orçamento") ou **ausente** — a SDR
 * não recebe a chave (FR-62); a tela decide pela presença da chave, nunca pelo perfil.
 */
export type NegocioDasEmpresas = Esquemas['RespostaDeNegocioDasEmpresas']

/** O teto do lote do serviço (`GET /negocios/das-empresas`), depois de tirar os repetidos. */
export const TETO_DO_LOTE_DE_EMPRESAS = 200

/** Os ids sem repetição e em ordem — a chave de cache não muda com a ordem em que chegaram. */
export function idsDoLote(ids: readonly string[]): string[] {
  return [...new Set(ids)].sort()
}

/**
 * Os Negócios das Empresas, em lotes de 200 (uma chamada por lote), unidos na ordem dos lotes.
 * `[]` sem id nenhum, sem ir à rede.
 */
export async function listarNegociosDasEmpresas(
  ids: readonly string[],
): Promise<NegocioDasEmpresas[]> {
  const distintos = idsDoLote(ids)
  const lotes: string[][] = []
  for (let inicio = 0; inicio < distintos.length; inicio += TETO_DO_LOTE_DE_EMPRESAS) {
    lotes.push(distintos.slice(inicio, inicio + TETO_DO_LOTE_DE_EMPRESAS))
  }
  const respostas = await Promise.all(
    lotes.map((lote) =>
      chamar<NegocioDasEmpresas[]>(
        'negocios',
        `/negocios/das-empresas?${lote
          .map((id) => `empresa_ids=${encodeURIComponent(id)}`)
          .join('&')}`,
      ),
    ),
  )
  return respostas.flat()
}

/** A consulta do lote, com a chave pelos ids ordenados e sem repetição. */
export function useNegociosDasEmpresas(ids: readonly string[]) {
  const distintos = idsDoLote(ids)
  return useQuery({
    queryKey: [...CHAVE_DOS_NEGOCIOS_DAS_EMPRESAS, ...distintos] as const,
    queryFn: () => listarNegociosDasEmpresas(distintos),
    enabled: distintos.length > 0,
    retry: false,
  })
}

/* --- O Orçamento do CRM: as formas de pagamento e a foto (13.22a2, FR-92) ------------------- */

/**
 * Uma forma de pagamento da lista configurável (Configuração → Negócios). A taxa é **percentual**
 * (`"6.00"`), e não dinheiro: quem a transforma em reais é a foto (`valor_no_cartao`).
 */
export type FormaDePagamento = Esquemas['RespostaDaFormaDePagamento']

/** O corpo de cadastrar e de editar a forma — completo, como o da Previsão. */
export type CorpoDaFormaDePagamento = Esquemas['CorpoDaFormaDePagamento']

/** A foto inteira: o que foi oferecido ao cliente, no instante em que foi gerada. */
export type OrcamentoDoCrm = Esquemas['RespostaDoOrcamentoDoCrm']

/**
 * Uma foto na lista do cartão: número, data, situação e os dois totais. Desde a 13.22b2 também quem
 * a gerou e o Responsável do instante (a tela esconde "Aprovar" e "Recusar" de quem não decide) e
 * `vigente` — a foto cujo total à vista é o valor do Negócio.
 */
export type ResumoDoOrcamentoDoCrm = Esquemas['RespostaDoResumoDoOrcamentoDoCrm']

/**
 * A situação da foto, derivada no serviço do que ela gravou: `pronta` até o teto e
 * `aguardando_aprovacao` acima dele (13.22b1); `aprovada` ou `recusada` pela decisão (13.22b2).
 * Enviada é da 13.22c1.
 */
export type SituacaoDoOrcamentoDoCrm = Esquemas['SituacaoDoOrcamentoDoCrm']

/** O corpo da decisão do desconto (13.22b2): aprovar ou recusar — e só, sem texto livre. */
export type CorpoDaDecisaoDoDesconto = Esquemas['CorpoDaDecisaoDoDesconto']

/** O pedido de "Gerar orçamento": a forma, o prazo de produção e o desconto (13.22b1) — e só. */
export type CorpoDoOrcamentoDoCrm = Esquemas['CorpoDoOrcamentoDoCrm']

/** O teto de desconto vigente (13.22b1): percentual em texto (`"5.00"`) e desde quando vale. */
export type TetoDeDesconto = Esquemas['RespostaDoTetoDeDesconto']

/** O corpo de configurar o teto: o percentual, e só. */
export type CorpoDoTetoDeDesconto = Esquemas['CorpoDoTetoDeDesconto']

export const CHAVE_DAS_FORMAS_DE_PAGAMENTO = ['negocios', 'formas-de-pagamento'] as const

function caminhoDaForma(formaId: string): string {
  return `/formas-de-pagamento/${encodeURIComponent(formaId)}`
}

/** Lê as formas **ativas** (`200`), por rótulo — todo perfil autenticado lê. */
export function lerFormasDePagamento(): Promise<FormaDePagamento[]> {
  return chamar<FormaDePagamento[]>('negocios', '/formas-de-pagamento')
}

/** O desfecho do cadastro: a forma, e se ela foi **reativada** (`200`, mesmo `id`). */
export interface FormaCadastrada {
  forma: FormaDePagamento
  restaurada: boolean
}

/** Cadastra a forma (`201`) — ou reativa a excluída com o mesmo rótulo (`200`). */
export async function cadastrarFormaDePagamento(
  nova: CorpoDaFormaDePagamento,
): Promise<FormaCadastrada> {
  const { corpo, status } = await chamarComStatus<FormaDePagamento>(
    'negocios',
    '/formas-de-pagamento',
    {
      method: 'POST',
      corpo: {
        rotulo: nova.rotulo,
        descricao: nova.descricao,
        taxa_percentual: nova.taxa_percentual,
      } satisfies CorpoDaFormaDePagamento,
    },
  )
  return { forma: corpo, restaurada: status === 200 }
}

/** Grava a forma como ela deve ficar (`200`). As fotos já geradas não mudam: guardam a cópia. */
export function editarFormaDePagamento(
  formaId: string,
  edicao: CorpoDaFormaDePagamento,
): Promise<FormaDePagamento> {
  return chamar<FormaDePagamento>('negocios', caminhoDaForma(formaId), {
    method: 'PUT',
    corpo: {
      rotulo: edicao.rotulo,
      descricao: edicao.descricao,
      taxa_percentual: edicao.taxa_percentual,
    } satisfies CorpoDaFormaDePagamento,
  })
}

/** Exclui a forma (`200` com `ativo: false`) — exclusão **lógica**. */
export function excluirFormaDePagamento(formaId: string): Promise<FormaDePagamento> {
  return chamar<FormaDePagamento>('negocios', caminhoDaForma(formaId), { method: 'DELETE' })
}

/** As formas ativas — o select do cartão e a lista da Configuração. */
export function useFormasDePagamento({ habilitada = true } = {}) {
  return useQuery({
    queryKey: CHAVE_DAS_FORMAS_DE_PAGAMENTO,
    queryFn: lerFormasDePagamento,
    enabled: habilitada,
    retry: false,
  })
}

function useInvalidarAsFormas() {
  const clienteDeConsultas = useQueryClient()
  return () => clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DAS_FORMAS_DE_PAGAMENTO })
}

/** Cadastra (ou reativa) a forma e relê a lista. */
export function useCadastrarFormaDePagamento() {
  const aoTerminar = useInvalidarAsFormas()
  return useMutation({
    mutationFn: (nova: CorpoDaFormaDePagamento) => cadastrarFormaDePagamento(nova),
    onSuccess: aoTerminar,
  })
}

/** Edita a forma e relê a lista. */
export function useEditarFormaDePagamento() {
  const aoTerminar = useInvalidarAsFormas()
  return useMutation({
    mutationFn: ({ formaId, edicao }: { formaId: string; edicao: CorpoDaFormaDePagamento }) =>
      editarFormaDePagamento(formaId, edicao),
    onSuccess: aoTerminar,
  })
}

/** Exclui logicamente a forma e relê a lista. */
export function useExcluirFormaDePagamento() {
  const aoTerminar = useInvalidarAsFormas()
  return useMutation({
    mutationFn: (formaId: string) => excluirFormaDePagamento(formaId),
    onSuccess: aoTerminar,
  })
}

/**
 * Chave das fotos de um Negócio. Ela fica **fora** de `CHAVE_DOS_NEGOCIOS` de propósito: a foto não
 * muda com escrita nenhuma do Negócio (é só de inserção), e sob o prefixo do quadro ela seria relida
 * a cada movimentação, nota ou releitura do valor. As exceções são explícitas: gerar, decidir
 * (13.22b2) e mudar o painel de Produtos, que muda qual foto é a vigente.
 */
export const chaveDasFotos = ({ negocioId }: { negocioId: string }) =>
  ['negocios', 'orcamentos-do-crm', negocioId] as const

function caminhoDasFotos(negocioId: string): string {
  return `/negocios/${encodeURIComponent(negocioId)}/orcamentos-do-crm`
}

/** Lê as fotos do Negócio (`200`), da mais nova para a mais velha. A SDR toma `403`. */
export function listarOrcamentosDoCrm(negocioId: string): Promise<ResumoDoOrcamentoDoCrm[]> {
  return chamar<ResumoDoOrcamentoDoCrm[]>('negocios', caminhoDasFotos(negocioId))
}

/**
 * Gera a foto (`201`). O preço vem do Carga Máquina neste ato, e número, data e autor são
 * carimbados pelo banco: o corpo leva a forma, o prazo e o desconto, e mais nada
 * (`extra="forbid"`). Se a foto exige aprovação quem decide é o banco, pelo teto (13.22b1).
 */
export function gerarOrcamentoDoCrm(
  negocioId: string,
  pedido: CorpoDoOrcamentoDoCrm,
): Promise<OrcamentoDoCrm> {
  return chamar<OrcamentoDoCrm>('negocios', caminhoDasFotos(negocioId), {
    method: 'POST',
    corpo: {
      forma_de_pagamento_id: pedido.forma_de_pagamento_id,
      prazo_de_producao: pedido.prazo_de_producao,
      desconto_percentual: pedido.desconto_percentual,
    } satisfies CorpoDoOrcamentoDoCrm,
  })
}

/** As fotos de um Negócio. `habilitada` falsa para a SDR e para o Negócio que está saindo. */
export function useOrcamentosDoCrm(negocioId: string, { habilitada = true } = {}) {
  return useQuery({
    queryKey: chaveDasFotos({ negocioId }),
    queryFn: () => listarOrcamentosDoCrm(negocioId),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Gera a foto e relê o que o ato mudou: a lista de fotos e — porque gerar relê os preços no ERP e
 * regrava as linhas, como abrir o painel — os Produtos e o valor do Negócio.
 */
export function useGerarOrcamentoDoCrm(negocioId: string) {
  const clienteDeConsultas = useQueryClient()
  const aoTerminarOsProdutos = useInvalidarOsProdutos(negocioId)
  return useMutation({
    mutationFn: (pedido: CorpoDoOrcamentoDoCrm) => gerarOrcamentoDoCrm(negocioId, pedido),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: chaveDasFotos({ negocioId }) })
      await aoTerminarOsProdutos()
    },
  })
}

/**
 * Aprova ou recusa o desconto de uma foto que espera aprovação (`201`, a foto com a situação nova).
 * Quem decide é o serviço: a SDR e quem não tem "Aprovar desconto" tomam `403`, e **nunca o
 * próprio** — quem gerou a foto e a Responsável, de agora ou do instante — também (`403`). A foto
 * que não espera aprovação, a já decidida e o Negócio fechado são `409`.
 */
export function decidirDesconto(
  negocioId: string,
  orcamentoId: string,
  decisao: CorpoDaDecisaoDoDesconto,
): Promise<OrcamentoDoCrm> {
  return chamar<OrcamentoDoCrm>(
    'negocios',
    `${caminhoDasFotos(negocioId)}/${encodeURIComponent(orcamentoId)}/decisao`,
    {
      method: 'POST',
      corpo: { aprovado: decisao.aprovado } satisfies CorpoDaDecisaoDoDesconto,
    },
  )
}

/**
 * Decide e relê o que a decisão mudou: a lista de fotos (a situação e a vigente) e o valor do
 * Negócio — aprovada, a foto pode passar a ser a vigente, e o valor da ficha, do quadro, do Hoje e
 * das Empresas muda com ela.
 */
export function useDecidirDesconto(negocioId: string) {
  const clienteDeConsultas = useQueryClient()
  const aoTerminar = useInvalidarOQuadroEOCatalogo()
  return useMutation({
    mutationFn: ({ orcamentoId, aprovado }: { orcamentoId: string; aprovado: boolean }) =>
      decidirDesconto(negocioId, orcamentoId, { aprovado }),
    onSuccess: async () => {
      await clienteDeConsultas.invalidateQueries({ queryKey: chaveDasFotos({ negocioId }) })
      await aoTerminar()
    },
  })
}

/* --- O teto de desconto (13.22b1, FR-93) ---------------------------------------------------- */

export const CHAVE_DO_TETO_DE_DESCONTO = ['negocios', 'teto-de-desconto'] as const

/** Lê o teto vigente (`200`). A SDR, de fábrica ou derivada, toma `403`. */
export function lerTetoDeDesconto(): Promise<TetoDeDesconto> {
  return chamar<TetoDeDesconto>('negocios', '/teto-de-desconto')
}

/**
 * Grava o teto (`200`). Exige `administrar_listas_previsao_e_campos`; a SDR toma `403` mesmo com
 * ela. Nenhuma foto muda: a situação de cada uma lê o que ela gravou.
 */
export function configurarTetoDeDesconto(teto: CorpoDoTetoDeDesconto): Promise<TetoDeDesconto> {
  return chamar<TetoDeDesconto>('negocios', '/teto-de-desconto', {
    method: 'PUT',
    corpo: { percentual: teto.percentual } satisfies CorpoDoTetoDeDesconto,
  })
}

/** O teto vigente — o cartão da Configuração e a frase do cartão "Orçamento do CRM". */
export function useTetoDeDesconto({ habilitada = true } = {}) {
  return useQuery({
    queryKey: CHAVE_DO_TETO_DE_DESCONTO,
    queryFn: lerTetoDeDesconto,
    enabled: habilitada,
    retry: false,
  })
}

/** Grava o teto e relê: a releitura, e não a resposta, é o que o cartão mostra. */
export function useConfigurarTetoDeDesconto() {
  const clienteDeConsultas = useQueryClient()
  return useMutation({
    mutationFn: (teto: CorpoDoTetoDeDesconto) => configurarTetoDeDesconto(teto),
    onSuccess: () =>
      clienteDeConsultas.invalidateQueries({
        queryKey: CHAVE_DO_TETO_DE_DESCONTO,
      }),
  })
}
